/**
 * DoserneTimesheetAdmin — "Control Contabil" (web, admin-only).
 *
 * A month-at-a-glance payroll grid: every employee on the left, the whole
 * month (1..31) across the top, worked hours in each cell. Click any day to
 * drill into the exact from/to segments and edit them inline. Tick 2–3
 * employees to compare their monthly totals side by side.
 *
 * Backend:
 *   GET  /doserne/timesheet/admin/month-grid?month=YYYY-MM
 *   GET  /doserne/timesheet/admin/entries?email=&start=&end=
 *   POST /doserne/timesheet/admin/entries        (create for a member)
 *   PUT  /doserne/timesheet/entries/{id}         (admin edit)
 *   DELETE /doserne/timesheet/entries/{id}
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Navigate } from "react-router-dom";
import { Loader2, ChevronLeft, ChevronRight, X, Plus, Trash2, Save, Calendar as CalIcon, Users, Coins, BarChart3, Search, FolderKanban, ChevronDown, Receipt, Printer, Lock, Unlock, ClipboardList, Link2, Download, Clock, Info, Copy, FileSpreadsheet, HelpCircle } from "lucide-react";
import { useDoserneAuth } from "./doserneAuth";
import { useLang } from "./i18n";
import DoserneShell from "./DoserneShell";
import OtAuditModal, { OtRulesInfo } from "./OtAuditModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TS = `${API}/doserne/timesheet`;

const WD = ["M", "T", "O", "T", "F", "L", "S"]; // Mon..Sun (no)

function thisMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}
const MONTH_KEY = "doserne_ts_admin_month";
function initialMonth() {
  try {
    const qp = new URLSearchParams(window.location.search).get("month");
    if (qp && /^\d{4}-\d{2}$/.test(qp)) return qp;
  } catch (e) { /* ignore */ }
  try {
    const saved = localStorage.getItem(MONTH_KEY);
    if (saved && /^\d{4}-\d{2}$/.test(saved)) return saved;
  } catch (e) { /* ignore */ }
  return thisMonth();
}
function shiftMonth(month, delta) {
  const [y, m] = month.split("-").map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}
function monthLabel(month, t) {
  const [y, m] = month.split("-").map(Number);
  const months = (t ? t("ts.months") : "").split(",");
  return `${months[m - 1] || m} ${y}`;
}
const h1 = (min) => (min / 60).toFixed(1).replace(/\.0$/, "");
const kr = (n) => new Intl.NumberFormat("no-NO", { maximumFractionDigits: 0 }).format(n || 0);

export default function DoserneTimesheetAdmin() {
  const { user, loading, authHeader } = useDoserneAuth();
  const { t } = useLang();
  const [month, setMonth] = useState(initialMonth());
  const [grid, setGrid] = useState(null);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState([]); // emails ticked for compare
  const [dayCtx, setDayCtx] = useState(null); // { email, name, date }
  const [payslipCtx, setPayslipCtx] = useState(null); // { email, name }
  const [hoursCtx, setHoursCtx] = useState(null); // { email, name } — hours export
  const [view, setView] = useState("employees"); // employees | projects
  const [syncTick, setSyncTick] = useState(0);
  const [dupModal, setDupModal] = useState(null); // { groups, groups_count, extra_entries } | 'loading'
  const [dupCells, setDupCells] = useState({});   // { "email|date": count } for the current month — triggers ⚠ on grid
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditEmail, setAuditEmail] = useState("");
  const [rulesOpen, setRulesOpen] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await axios.get(`${TS}/admin/month-grid`, { params: { month }, ...authHeader() });
      setGrid(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("ts.load_err"));
      setGrid({ month, days: [], members: [] });
    } finally {
      setBusy(false);
    }
  }, [month, authHeader, t]);

  useEffect(() => { if (user?.is_admin) load(); }, [user, load]);

  // Scan the current month for duplicates every time it changes — paints a ⚠
  // triangle on affected cells so the admin spots them without opening the modal.
  useEffect(() => {
    if (!user?.is_admin) return;
    let dead = false;
    (async () => {
      try {
        const { data } = await axios.get(`${TS}/admin/duplicate-month?month=${month}`, authHeader());
        if (!dead) setDupCells(data?.cells || {});
      } catch { if (!dead) setDupCells({}); }
    })();
    return () => { dead = true; };
  }, [user, month, syncTick, authHeader]);

  const openDupScan = async () => {
    setDupModal("loading");
    try {
      const [scan, batches] = await Promise.all([
        axios.get(`${TS}/admin/duplicate-scan`, authHeader()),
        axios.get(`${TS}/admin/duplicate-batches`, authHeader()),
      ]);
      setDupModal({ ...scan.data, batches: batches.data?.batches || [] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Scanare eșuată");
      setDupModal(null);
    }
  };
  const runDedupe = async () => {
    if (!window.confirm("Mut în coș copiile duplicate (păstrez cea mai veche din fiecare grup). Pot fi restaurate oricând de aici.")) return;
    try {
      const { data } = await axios.post(`${TS}/admin/duplicate-dedupe`, {}, authHeader());
      toast.success(`${data.deleted} intrări duplicate mutate în coș. Poți restaura dacă ceva e greșit.`);
      openDupScan();
      load();
      setSyncTick((n) => n + 1);  // refresh the ⚠ triangles after dedupe
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Ștergere eșuată");
    }
  };
  const restoreBatch = async (batchId) => {
    if (!window.confirm("Restaurez acest lot (readaug intrările în pontaj)?")) return;
    try {
      const { data } = await axios.post(`${TS}/admin/duplicate-restore`, { batch_id: batchId }, authHeader());
      toast.success(`${data.restored} intrări restaurate.`);
      openDupScan();
      load();
      setSyncTick((n) => n + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Restaurare eșuată");
    }
  };
  const downloadBackup = () => {
    if (!dupModal || dupModal === "loading") return;
    const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
    const blob = new Blob([JSON.stringify(dupModal, null, 2)], { type: "application/json" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `pontaj-duplicate-scan-${stamp}.json`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  // Persist selected month to localStorage + URL so refresh keeps the chosen month
  useEffect(() => {
    try { localStorage.setItem(MONTH_KEY, month); } catch (e) { /* ignore */ }
    try {
      const url = new URL(window.location.href);
      url.searchParams.set("month", month);
      window.history.replaceState({}, "", url);
    } catch (e) { /* ignore */ }
  }, [month]);

  const toggleSelect = (email) => {
    setSelected((cur) => {
      if (cur.includes(email)) return cur.filter((e) => e !== email);
      if (cur.length >= 3) { toast.info(t("ts.cmp_max")); return cur; }
      return [...cur, email];
    });
  };

  if (loading) return <DoserneShell><div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div></DoserneShell>;
  if (!user) return <Navigate to="/doserne/logg-inn" replace />;
  if (!user.is_admin) return <Navigate to="/doserne" replace />;

  const days = grid?.days || [];
  const members = grid?.members || [];
  const compareRows = members.filter((m) => selected.includes(m.email));

  // Build the column list, inserting a weekly-subtotal column after each Sunday
  // (and after the last day of the month, to close the final partial week).
  const buildCols = (ds) => {
    const out = [];
    let grp = [];
    ds.forEach((d, idx) => {
      out.push({ type: "day", d });
      grp.push(d.date);
      if (d.weekday === 6 || idx === ds.length - 1) {
        out.push({ type: "week", dates: grp, key: `wk-${d.date}` });
        grp = [];
      }
    });
    return out;
  };
  const cols = buildCols(days);
  const weekSub = (mem, dates) => {
    let n = 0, ov = 0;
    dates.forEach((dt) => {
      const c = mem.days?.[dt];
      if (c) { n += c.normal_minutes || 0; ov += (c.ot50_minutes || 0) + (c.ot100_minutes || 0); }
    });
    return { n, ov };
  };

  return (
    <DoserneShell wide>
      <div className="mb-4 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-1 mb-1.5">
            <Coins className="w-3 h-3" /> {t("ts.badge")}
          </div>
          <h1 className="text-3xl sm:text-4xl font-black text-slate-900 tracking-tight">{t("ts.title")}</h1>
          <p className="text-sm text-slate-500 mt-1">{t("ts.subtitle")}</p>
        </div>
        <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-xl px-2 py-1.5 shadow-sm">
          <button onClick={() => setMonth((m) => shiftMonth(m, -1))} className="w-9 h-9 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-600" data-testid="ts-admin-prev-month"><ChevronLeft className="w-5 h-5" /></button>
          <div className="inline-flex items-center gap-2 min-w-[130px] justify-center">
            <CalIcon className="w-4 h-4 text-slate-400" />
            <span className="text-sm font-black text-slate-900 capitalize" data-testid="ts-admin-month-label">{monthLabel(month, t)}</span>
          </div>
          <button onClick={() => setMonth((m) => shiftMonth(m, 1))} className="w-9 h-9 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-600" data-testid="ts-admin-next-month"><ChevronRight className="w-5 h-5" /></button>
          <div className="w-px h-6 bg-slate-200 mx-0.5" />
          <button onClick={openDupScan} title="Verifică ore duplicate" data-testid="ts-admin-dup-scan"
            className={`inline-flex items-center gap-1.5 h-9 px-2.5 rounded-lg text-xs font-bold ${Object.keys(dupCells).length ? "bg-amber-100 text-amber-800 hover:bg-amber-200 animate-pulse" : "hover:bg-amber-50 text-slate-600 hover:text-amber-700"}`}>
            {Object.keys(dupCells).length > 0 ? <span className="text-base leading-none">⚠</span> : <Copy className="w-4 h-4" />}
            Duplicate{Object.keys(dupCells).length ? ` (${Object.keys(dupCells).length})` : ""}
          </button>
          <button onClick={() => setAuditOpen(true)} title="Audit OT (reconciliere ore suplimentare)" data-testid="ts-admin-ot-audit"
            className="inline-flex items-center gap-1.5 h-9 px-2.5 rounded-lg text-xs font-bold hover:bg-cyan-50 text-slate-600 hover:text-cyan-700">
            <FileSpreadsheet className="w-4 h-4" />
            Audit OT
          </button>
          <button onClick={() => setRulesOpen(true)} title="Vezi toate criteriile de calcul OT" data-testid="ts-admin-ot-rules"
            className="inline-flex items-center gap-1.5 h-9 px-2.5 rounded-lg text-xs font-bold hover:bg-cyan-50 text-slate-600 hover:text-cyan-700">
            <HelpCircle className="w-4 h-4" />
            Criterii OT
          </button>
        </div>
      </div>

      <div className="mb-4 flex items-center gap-2 flex-wrap">
      <div className="inline-flex rounded-xl border border-slate-200 bg-white p-1 shadow-sm" data-testid="ts-admin-view-toggle">
        <button
          onClick={() => setView("employees")}
          className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-sm font-bold transition ${view === "employees" ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-800"}`}
          data-testid="ts-view-employees"
        >
          <Users className="w-4 h-4" /> {t("ts.tab_employees")}
        </button>
        <button
          onClick={() => setView("projects")}
          className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-sm font-bold transition ${view === "projects" ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-800"}`}
          data-testid="ts-view-projects"
        >
          <FolderKanban className="w-4 h-4" /> {t("ts.tab_projects")}
        </button>
        <button
          onClick={() => setView("review")}
          className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-sm font-bold transition ${view === "review" ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-800"}`}
          data-testid="ts-view-review"
        >
          <ClipboardList className="w-4 h-4" /> {t("ts.tab_review")}
        </button>
      </div>
        <TripletexImportButton month={month} authHeader={authHeader} t={t} onDone={() => { load(); setSyncTick((n) => n + 1); }} />
        <LinkEmployeesButton authHeader={authHeader} t={t} />
        <BackfillButton authHeader={authHeader} t={t} onDone={() => { load(); setSyncTick((n) => n + 1); }} />
      </div>

      <SyncHistory authHeader={authHeader} t={t} refreshKey={syncTick} />
      <BreakCodeCreate authHeader={authHeader} month={month} />
      <SalaryExportPanel authHeader={authHeader} month={month} />
      <BreakCodeLog authHeader={authHeader} t={t} month={month} />

      {view === "projects" ? (
        <ProjectsView month={month} authHeader={authHeader} t={t} />
      ) : view === "review" ? (
        <ReviewView month={month} authHeader={authHeader} t={t} members={members} />
      ) : (
      <>
      {selected.length >= 2 && (
        <CompareCard rows={compareRows} days={days} t={t} onClear={() => setSelected([])} onDayClick={(email, name, date) => setDayCtx({ email, name, date })} />
      )}

      <WeekLockBar month={month} authHeader={authHeader} t={t} />

      <MonthTotalsPanel month={month} authHeader={authHeader} t={t} />


      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto" data-testid="ts-admin-grid">
          {busy && !grid ? (
            <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
          ) : members.length === 0 ? (
            <div className="text-center py-16 text-sm text-slate-400 italic">{t("ts.empty_members")}</div>
          ) : (
            <table className="border-collapse text-xs w-full">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="sticky left-0 z-20 bg-slate-50 text-left px-3 py-2 font-black text-slate-500 uppercase tracking-wider min-w-[190px] border-r border-slate-200">{t("ts.col_employee")}</th>
                  {cols.map((c) => {
                    if (c.type === "week") {
                      return (
                        <th key={c.key} className="px-1 py-1 text-center bg-slate-200/70 border-l border-r border-slate-300 min-w-[40px]" title="Subtotal săptămână">
                          <div className="text-[8px] leading-tight font-black text-slate-600 uppercase">Norm.</div>
                          <div className="text-[8px] leading-tight font-black text-amber-600 uppercase">OV</div>
                        </th>
                      );
                    }
                    const d = c.d;
                    const dn = d.date.slice(8);
                    const isRed = d.red || d.weekday === 6; const sat = d.weekday === 5;
                    return (
                      <th key={d.date} className={`px-0 py-1 text-center font-bold min-w-[30px] ${isRed ? "bg-rose-50 text-rose-500" : sat ? "bg-slate-50 text-slate-400" : "text-slate-500"}`} title={d.red_reason || ""}>
                        <div className="text-[9px] leading-none">{WD[d.weekday]}</div>
                        <div className="text-[11px] leading-tight">{dn}</div>
                      </th>
                    );
                  })}
                  <th className="px-2 py-2 text-center font-black text-slate-500 uppercase tracking-wider border-l border-slate-200 min-w-[52px]">{t("ts.col_norm")}</th>
                  <th className="px-2 py-2 text-center font-black text-amber-600 uppercase tracking-wider min-w-[46px]" title="Overtime 50%">{t("ts.col_ot50")}</th>
                  <th className="px-2 py-2 text-center font-black text-rose-600 uppercase tracking-wider min-w-[46px]" title="Overtime 100%">{t("ts.col_ot100")}</th>
                  <th className="px-2 py-2 text-center font-black text-sky-600 uppercase tracking-wider min-w-[42px]">{t("ts.col_fer")}</th>
                  <th className="px-2 py-2 text-center font-black text-violet-600 uppercase tracking-wider min-w-[42px]">{t("ts.col_boa")}</th>
                  <th className="px-2 py-2 text-right font-black text-emerald-700 uppercase tracking-wider min-w-[80px]">{t("ts.col_cost")}</th>
                  <th className="px-2 py-2 text-center font-black text-orange-600 uppercase tracking-wider min-w-[64px]" title={t("ts.col_missing_title")}>{t("ts.col_missing")}</th>
                </tr>
              </thead>
              <tbody>
                {members.map((mem) => {
                  const isSel = selected.includes(mem.email);
                  return (
                    <tr key={mem.email} className={`border-b border-slate-100 last:border-0 ${isSel ? "bg-cyan-50/50" : "hover:bg-slate-50/60"}`} data-testid={`ts-admin-row-${mem.email}`}>
                      <td className={`sticky left-0 z-10 px-3 py-1.5 border-r border-slate-200 ${isSel ? "bg-cyan-50" : "bg-white"}`}>
                        <div className="flex items-center gap-1.5">
                          <label className="flex items-center gap-2 cursor-pointer flex-1 min-w-0">
                            <input type="checkbox" checked={isSel} onChange={() => toggleSelect(mem.email)} className="w-3.5 h-3.5 accent-cyan-600 flex-shrink-0" data-testid={`ts-admin-cmp-${mem.email}`} />
                            <span className="min-w-0">
                              <span className="block text-[12px] font-black text-slate-900 truncate max-w-[130px]">{mem.name}</span>
                              <span className="block text-[10px] text-slate-400">{mem.hourly_cost > 0 ? `${kr(mem.hourly_cost)} kr/t` : "— kr/t"}</span>
                            </span>
                          </label>
                          <button type="button" onClick={() => setPayslipCtx({ email: mem.email, name: mem.name })} title={t("ts.payslip_btn_title")} className="w-7 h-7 rounded-lg bg-slate-100 hover:bg-indigo-100 text-slate-500 hover:text-indigo-700 flex items-center justify-center flex-shrink-0" data-testid={`ts-payslip-${mem.email}`}>
                            <Receipt className="w-3.5 h-3.5" />
                          </button>
                          <button type="button" onClick={() => setHoursCtx({ email: mem.email, name: mem.name })} title={t("ts.hours_export_title")} className="w-7 h-7 rounded-lg bg-slate-100 hover:bg-cyan-100 text-slate-500 hover:text-cyan-700 flex items-center justify-center flex-shrink-0" data-testid={`ts-hours-${mem.email}`}>
                            <Clock className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                      {cols.map((c) => {
                        if (c.type === "week") {
                          const { n, ov } = weekSub(mem, c.dates);
                          return (
                            <td key={c.key} className="px-1 py-1 text-center bg-slate-100 border-l border-r border-slate-200 align-middle" data-testid={`ts-weeksub-${mem.email}-${c.key}`}>
                              <div className="text-[11px] leading-tight font-black text-slate-700">{n ? h1(n) : "–"}</div>
                              <div className={`text-[11px] leading-tight font-bold ${ov ? "text-amber-600" : "text-slate-300"}`}>{ov ? h1(ov) : "–"}</div>
                            </td>
                          );
                        }
                        const d = c.d;
                        const cell = mem.days?.[d.date];
                        const isRed = d.red || d.weekday === 6; const sat = d.weekday === 5;
                        return (
                          <td key={d.date} className={`p-0 text-center align-middle border-l border-slate-50 ${isRed ? "bg-rose-50/40" : sat ? "bg-slate-50/60" : ""}`}>
                            <DayCell cell={cell} t={t} dupCount={dupCells[`${mem.email}|${d.date}`] || 0} onClick={() => setDayCtx({ email: mem.email, name: mem.name, date: d.date })} testid={`ts-cell-${mem.email}-${d.date}`} />
                          </td>
                        );
                      })}
                      <td className="px-2 py-1.5 text-center font-black text-slate-800 border-l border-slate-200">{mem.totals.normal_minutes ? h1(mem.totals.normal_minutes) : "–"}</td>
                      <td className="px-2 py-1.5 text-center font-bold text-amber-600">{mem.totals.ot50_minutes ? h1(mem.totals.ot50_minutes) : "–"}</td>
                      <td className="px-2 py-1.5 text-center font-bold text-rose-600">{mem.totals.ot100_minutes ? h1(mem.totals.ot100_minutes) : "–"}</td>
                      <td className="px-2 py-1.5 text-center font-bold text-sky-600">{mem.totals.vacation_days || "–"}</td>
                      <td className="px-2 py-1.5 text-center font-bold text-violet-600">{mem.totals.sick_days || "–"}</td>
                      <td className="px-2 py-1.5 text-right whitespace-nowrap" title="Click pentru detalii cost și cum sunt calculate orele">
                        <button type="button" onClick={() => { setAuditEmail(mem.email); setAuditOpen(true); }}
                          className="inline-flex items-center gap-1 font-black text-emerald-700 hover:underline hover:text-emerald-900"
                          data-testid={`ts-row-cost-${mem.email}`}>
                          {mem.totals.labor_cost ? `${kr(mem.totals.labor_cost)} kr` : "–"}
                          <Info className="w-3 h-3 opacity-50" />
                        </button>
                      </td>
                      <td className="px-2 py-1.5 text-center whitespace-nowrap" title={t("ts.col_missing_title")}>
                        <span className={`font-black ${mem.totals.missing_days_month > 0 ? "text-orange-600" : "text-slate-300"}`}>{mem.totals.missing_days_month || 0}</span>
                        <span className="text-slate-300 font-bold"> / </span>
                        <span className={`font-bold text-[11px] ${mem.totals.missing_days_year > 0 ? "text-orange-500" : "text-slate-300"}`}>{mem.totals.missing_days_year || 0}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11px] text-slate-500">
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-slate-800" /> {t("ts.legend_normal")}</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-amber-400" /> {t("ts.legend_ot50")}</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-rose-500" /> {t("ts.legend_ot100")}</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-sky-400" /> {t("ts.legend_ferie")}</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-violet-400" /> {t("ts.legend_sick")}</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-rose-200" /> {t("ts.legend_red")}</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-slate-200" /> {t("ts.legend_sat")}</span>
        <span className="inline-flex items-center gap-1.5"><span className="text-[9px] font-black text-sky-600 bg-sky-100 rounded px-1">D</span> {t("ts.legend_depot")}</span>
        <span className="ml-auto inline-flex items-center gap-1"><Users className="w-3 h-3" /> {t("ts.legend_compare")}</span>
      </div>
      <div className="mt-2 flex items-start gap-1.5 text-[11px] text-slate-500 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="ts-ot-rule-note">
        <Info className="w-3.5 h-3.5 text-amber-600 shrink-0 mt-0.5" />
        <span>{t("ts.ot_rule_note")}</span>
      </div>
      </>
      )}

      {dayCtx && (
        <DayEditor
          ctx={dayCtx}
          authHeader={authHeader}
          t={t}
          onClose={() => setDayCtx(null)}
          onChanged={() => { setDayCtx(null); load(); }}
        />
      )}

      {payslipCtx && (
        <PayslipModal ctx={payslipCtx} month={month} authHeader={authHeader} t={t} onClose={() => setPayslipCtx(null)} />
      )}
      {hoursCtx && (
        <HoursExportModal ctx={hoursCtx} month={month} authHeader={authHeader} t={t} onClose={() => setHoursCtx(null)} />
      )}
      <OtAuditModal open={auditOpen} onClose={() => { setAuditOpen(false); setAuditEmail(""); }} members={members} month={month} authHeader={authHeader} defaultEmail={auditEmail} />
      {rulesOpen && <OtRulesInfo onClose={() => setRulesOpen(false)} />}
      {dupModal && (
        <div className="fixed inset-0 z-[80] bg-slate-900/40 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" data-testid="dup-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl mt-10">
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
              <div>
                <div className="text-[10px] font-black uppercase tracking-widest text-amber-700">Verificare duplicate</div>
                <h2 className="text-lg font-black text-slate-900">Intrări identice pe aceeași zi</h2>
              </div>
              <button onClick={() => setDupModal(null)} className="w-9 h-9 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-500"><X className="w-5 h-5" /></button>
            </div>
            {dupModal === "loading" ? (
              <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400 inline-block" /></div>
            ) : (dupModal.groups_count === 0 ? (
              <div className="px-5 py-10 text-center">
                <div className="text-5xl mb-3">✅</div>
                <div className="text-lg font-black text-slate-900">Zero duplicate</div>
                <p className="text-sm text-slate-500 mt-1">Toate orele din pontaj sunt unice. Nu e nimic de curățat.</p>
                {dupModal.batches && dupModal.batches.length > 0 && (
                  <div className="mt-5 text-left border-t border-slate-100 pt-4">
                    <div className="text-xs font-black uppercase tracking-wider text-slate-500 mb-2">Loturi recente — poți restaura</div>
                    <div className="space-y-1.5">
                      {dupModal.batches.map((b) => (
                        <div key={b.batch_id} className="flex items-center justify-between gap-2 border border-slate-200 rounded-lg px-3 py-2 text-sm">
                          <div>
                            <div className="font-bold text-slate-800">{b.count} intrări · {(b.deleted_at || "").slice(0, 16).replace("T", " ")}</div>
                            <div className="text-[11px] text-slate-500">de {b.deleted_by} · {b.users.slice(0, 3).join(", ")}{b.users.length > 3 ? ` +${b.users.length - 3}` : ""}</div>
                          </div>
                          <button onClick={() => restoreBatch(b.batch_id)} data-testid={`dup-restore-${b.batch_id}`}
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-emerald-100 hover:bg-emerald-200 text-emerald-800 text-xs font-black">
                            ↩ Restaurează
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <>
                <div className="px-5 py-3 bg-amber-50 border-b border-amber-200">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="text-sm text-amber-900"><b>{dupModal.extra_entries}</b> copii extra în <b>{dupModal.groups_count}</b> grupuri. Se păstrează cea mai veche intrare din fiecare grup.</div>
                    <div className="flex items-center gap-2">
                      <button onClick={downloadBackup} data-testid="dup-backup"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 text-xs font-black">
                        <Download className="w-4 h-4" /> Backup JSON
                      </button>
                      <button onClick={runDedupe} data-testid="dup-run-dedupe"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-xs font-black shadow">
                        <Trash2 className="w-4 h-4" /> Mută în coș
                      </button>
                    </div>
                  </div>
                  <div className="mt-1.5 text-[11px] text-amber-800/80">💡 Siguranță: nu se șterge definitiv — intrările merg într-un coș de reciclare și pot fi restaurate oricând din acest ecran.</div>
                </div>
                <div className="max-h-[55vh] overflow-y-auto px-5 py-3 space-y-2.5">
                  {dupModal.groups.slice(0, 50).map((g, i) => (
                    <div key={i} className="border border-slate-200 rounded-lg px-3 py-2.5">
                      <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div className="text-sm">
                          <span className="font-black text-slate-900">{g.entries[0]?.user_name || g.user_email}</span>
                          <span className="text-slate-400"> · {g.date} · {g.from_time}–{g.to_time}</span>
                        </div>
                        <span className="text-[10px] font-black uppercase tracking-wider text-amber-700 bg-amber-100 border border-amber-200 rounded-full px-2 py-0.5">{g.count} copii</span>
                      </div>
                      <div className="mt-2 grid gap-1">
                        {g.entries.map((e, j) => (
                          <div key={j} className="text-[11px] text-slate-500 flex items-center justify-between">
                            <span>#{j + 1} · {e.project_name || "—"} {e.activity_name ? `· ${e.activity_name}` : ""}</span>
                            <span className="text-slate-400">{(e.source || "manual")} · {(e.created_at || "").slice(0, 16).replace("T", " ")}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                  {dupModal.groups.length > 50 && (<div className="text-xs text-slate-400 italic text-center py-2">+{dupModal.groups.length - 50} grupuri în plus (toate incluse la click)</div>)}
                </div>
                {dupModal.batches && dupModal.batches.length > 0 && (
                  <div className="border-t border-slate-100 px-5 py-3">
                    <div className="text-xs font-black uppercase tracking-wider text-slate-500 mb-2">Loturi recente — poți restaura</div>
                    <div className="space-y-1.5">
                      {dupModal.batches.map((b) => (
                        <div key={b.batch_id} className="flex items-center justify-between gap-2 border border-slate-200 rounded-lg px-3 py-2 text-sm">
                          <div>
                            <div className="font-bold text-slate-800">{b.count} intrări · {(b.deleted_at || "").slice(0, 16).replace("T", " ")}</div>
                            <div className="text-[11px] text-slate-500">de {b.deleted_by} · {b.users.slice(0, 3).join(", ")}{b.users.length > 3 ? ` +${b.users.length - 3}` : ""}</div>
                          </div>
                          <button onClick={() => restoreBatch(b.batch_id)} data-testid={`dup-restore-${b.batch_id}`}
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-emerald-100 hover:bg-emerald-200 text-emerald-800 text-xs font-black">
                            ↩ Restaurează
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ))}
          </div>
        </div>
      )}
    </DoserneShell>
  );
}

function DayCell({ cell, onClick, testid, t, dupCount = 0 }) {
  let content = null;
  let cls = "text-slate-300";
  if (cell) {
    if (cell.kind === "vacation") { content = t ? t("ts.abbr_vacation") : "F"; cls = "text-sky-600 font-black"; }
    else if (cell.kind === "sick") { content = t ? t("ts.abbr_sick") : "S"; cls = "text-violet-600 font-black"; }
    else if (cell.worked_minutes > 0) {
      content = h1(cell.worked_minutes);
      if (cell.ot100_minutes > 0) cls = "text-rose-600 font-black";       // 100% (red day / Sunday)
      else if (cell.ot50_minutes > 0) cls = "text-amber-600 font-black";  // 50% overtime
      else cls = "text-slate-800 font-bold";
    }
  }
  const isDepot = cell && cell.depot_minutes > 0;
  const isPaidBreak = cell && cell.break_status === "paid";
  return (
    <button
      type="button"
      onClick={onClick}
      title={dupCount ? `⚠ ${dupCount} ${dupCount === 1 ? "intrare duplicată" : "intrări duplicate"} — click pentru verificare` : (isPaidBreak ? "☕ Pauză plătită (nu împinge spre OT)" : (isDepot ? (t ? t("ts.depot_tooltip") : "Depot — no overtime") : undefined))}
      className={`relative w-full h-9 flex items-center justify-center text-[11px] hover:bg-cyan-100/60 transition ${cls} ${dupCount ? "bg-amber-100/70 ring-1 ring-inset ring-amber-400" : ""}`}
      data-testid={testid}
    >
      {content || <span className="text-slate-200">·</span>}
      {isDepot && !dupCount && (
        <span
          className="absolute top-0.5 right-0.5 text-[7px] leading-none font-black text-sky-600 bg-sky-100 rounded px-0.5 py-px"
          data-testid={`${testid}-depot`}
        >
          D
        </span>
      )}
      {isPaidBreak && !dupCount && (
        <span
          className="absolute bottom-0.5 right-0.5 text-[9px] leading-none"
          data-testid={`${testid}-paid-break`}
        >
          ☕
        </span>
      )}
      {dupCount > 0 && (
        <span
          className="absolute -top-0.5 -right-0.5 text-[9px] leading-none font-black text-amber-900 bg-amber-300 rounded-sm px-0.5 py-px shadow-sm animate-pulse"
          data-testid={`${testid}-dup`}
          title={`${dupCount} duplicate`}
        >
          ⚠
        </span>
      )}
    </button>
  );
}

function CompareCard({ rows, days, onClear, onDayClick, t }) {
  const palette = ["bg-cyan-500", "bg-emerald-500", "bg-amber-500"];
  const dot = ["bg-cyan-500", "bg-emerald-500", "bg-amber-500"];
  return (
    <div className="mb-4 bg-white rounded-2xl border border-cyan-200 shadow-sm overflow-hidden" data-testid="ts-admin-compare">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
        <div className="inline-flex items-center gap-1.5 text-sm font-black text-slate-900"><BarChart3 className="w-4 h-4 text-cyan-600" /> {t("ts.cmp_title")}</div>
        <button onClick={onClear} className="text-xs font-bold text-slate-400 hover:text-slate-700 inline-flex items-center gap-1" data-testid="ts-admin-compare-clear"><X className="w-3.5 h-3.5" /> {t("ts.cmp_clear")}</button>
      </div>

      {/* Per-day calendar comparison — month across the top, hours per day below for each employee */}
      <div className="overflow-x-auto" data-testid="ts-compare-grid">
        <table className="border-collapse text-xs w-full">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              <th className="sticky left-0 z-10 bg-slate-50 text-left px-3 py-2 font-black text-slate-500 uppercase tracking-wider min-w-[170px] border-r border-slate-200">{t("ts.col_employee")}</th>
              {days.map((d) => {
                const isRed = d.red || d.weekday === 6; const sat = d.weekday === 5;
                return (
                  <th key={d.date} className={`px-0 py-1 text-center font-bold min-w-[30px] ${isRed ? "bg-rose-50 text-rose-500" : sat ? "bg-slate-50 text-slate-400" : "text-slate-500"}`} title={d.red_reason || ""}>
                    <div className="text-[9px] leading-none">{WD[d.weekday]}</div>
                    <div className="text-[11px] leading-tight">{d.date.slice(8)}</div>
                  </th>
                );
              })}
              <th className="px-2 py-2 text-center font-black text-slate-500 uppercase tracking-wider border-l border-slate-200 min-w-[52px]">Total</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.email} className="border-b border-slate-100 last:border-0" data-testid={`ts-cmp-row-${r.email}`}>
                <td className="sticky left-0 z-10 bg-white px-3 py-1.5 border-r border-slate-200">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${dot[i % dot.length]}`} />
                    <span className="min-w-0">
                      <span className="block text-[12px] font-black text-slate-900 truncate max-w-[130px]">{r.name}</span>
                      <span className="block text-[10px] font-bold"><span className="text-amber-600">{h1(r.totals.ot50_minutes)}t {t("ts.cmp_ot50_short")}</span> · <span className="text-rose-600">{h1(r.totals.ot100_minutes)}t {t("ts.cmp_ot100_short")}</span> · <span className="text-emerald-700">{kr(r.totals.labor_cost)} kr</span></span>
                    </span>
                  </div>
                </td>
                {days.map((d) => {
                  const cell = r.days?.[d.date];
                  const isRed = d.red || d.weekday === 6; const sat = d.weekday === 5;
                  return (
                    <td key={d.date} className={`p-0 text-center align-middle border-l border-slate-50 ${isRed ? "bg-rose-50/40" : sat ? "bg-slate-50/60" : ""}`}>
                      <DayCell cell={cell} t={t} onClick={() => onDayClick(r.email, r.name, d.date)} testid={`ts-cmp-cell-${r.email}-${d.date}`} />
                    </td>
                  );
                })}
                <td className="px-2 py-1.5 text-center font-black text-slate-800 border-l border-slate-200">{r.totals.worked_minutes ? h1(r.totals.worked_minutes) : "–"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Total worked bars for a quick side-by-side magnitude read */}
      <div className="p-4 space-y-2 border-t border-slate-100 bg-slate-50/40">
        {rows.map((r, i) => {
          const max = Math.max(1, ...rows.map((x) => x.totals.worked_minutes));
          return (
            <div key={r.email}>
              <div className="flex items-center justify-between text-[11px] mb-0.5">
                <span className="font-bold text-slate-700 truncate">{r.name}</span>
                <span className="text-slate-500">{h1(r.totals.worked_minutes)}t</span>
              </div>
              <div className="h-2.5 rounded-full bg-slate-200/70 overflow-hidden">
                <div className={`h-full ${palette[i % palette.length]} rounded-full transition-all`} style={{ width: `${(r.totals.worked_minutes / max) * 100}%` }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// DayEditor — drill into a single employee-day: list every from/to segment,
// edit times inline, add/remove segments. Admin-only endpoints.
// -----------------------------------------------------------------------------
function DayEditor({ ctx, authHeader, onClose, onChanged, t }) {
  const { email, name, date } = ctx;
  const [entries, setEntries] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dayOt, setDayOt] = useState(null);

  const load = useCallback(async () => {
    try {
      const [res, ot] = await Promise.all([
        axios.get(`${TS}/admin/entries`, { params: { email, start: date, end: date }, ...authHeader() }),
        axios.get(`${TS}/admin/day-ot`, { params: { email, date }, ...authHeader() }).catch(() => null),
      ]);
      setEntries((res.data.entries || []).map((e) => ({ ...e, _dirty: false })));
      setDayOt(ot ? ot.data : null);
    } catch {
      setEntries([]);
    }
  }, [email, date, authHeader]);

  useEffect(() => { load(); }, [load]);

  const setField = (id, key, val) => {
    setEntries((cur) => cur.map((e) => (e.id === id ? { ...e, [key]: val, _dirty: true } : e)));
  };

  const addSegment = () => {
    setEntries((cur) => {
      const list = cur || [];
      // Start the new segment where the latest one ended (by end time).
      const span = (f, t) => { const [fh, fm] = (f || "0:0").split(":").map(Number); let [th, tm] = (t || "0:0").split(":").map(Number); let a = fh * 60 + fm, b = th * 60 + tm; if (b <= a) b += 1440; return b; };
      let lastEnd = "";
      let maxEnd = -1;
      for (const e of list) {
        const end = span(e.from_time, e.to_time) % 1440;
        if (span(e.from_time, e.to_time) > maxEnd) { maxEnd = span(e.from_time, e.to_time); lastEnd = `${String(Math.floor(end / 60)).padStart(2, "0")}:${String(end % 60).padStart(2, "0")}`; }
      }
      const from = lastEnd || "07:00";
      return [...list, { id: `new-${Date.now()}`, from_time: from, to_time: from, kind: "work", comment: "", project_name: "", _new: true, _dirty: true }];
    });
  };

  const removeSegment = async (entry) => {
    if (entry._new) { setEntries((cur) => cur.filter((e) => e.id !== entry.id)); return; }
    if (!window.confirm(t("ts.seg_del_confirm"))) return;
    try {
      await axios.delete(`${TS}/entries/${entry.id}`, authHeader());
      setEntries((cur) => cur.filter((e) => e.id !== entry.id));
      toast.success(t("ts.seg_deleted"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("ts.seg_del_fail"));
    }
  };

  const saveAll = async () => {
    const dirty = (entries || []).filter((e) => e._dirty);
    if (dirty.length === 0) { onClose(); return; }
    // Client-side overlap guard — don't let the same hours be logged twice.
    const span = (f, t) => { const [fh, fm] = f.split(":").map(Number); let [th, tm] = t.split(":").map(Number); let a = fh * 60 + fm, b = th * 60 + tm; if (b <= a) b += 1440; return [a, b]; };
    const list = (entries || []).filter((e) => e.kind !== undefined);
    for (let i = 0; i < list.length; i++) {
      for (let j = i + 1; j < list.length; j++) {
        const [a1, a2] = span(list[i].from_time, list[i].to_time);
        const [b1, b2] = span(list[j].from_time, list[j].to_time);
        if (a1 < b2 && b1 < a2) {
          toast.error(t("ts.overlap", { a: `${list[i].from_time}–${list[i].to_time}`, b: `${list[j].from_time}–${list[j].to_time}` }));
          return;
        }
      }
    }
    setSaving(true);
    try {
      for (const e of dirty) {
        const body = { date, from_time: e.from_time, to_time: e.to_time, is_break: false, kind: e.kind || "work", project_id: e.project_id || "", activity_id: e.activity_id || "", comment: e.comment || "", overtime: !!e.overtime };
        if (e._new) {
          await axios.post(`${TS}/admin/entries`, { ...body, email }, authHeader());
        } else {
          await axios.put(`${TS}/entries/${e.id}`, body, authHeader());
        }
      }
      toast.success(t("ts.saved"));
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("ts.save_fail"));
      setSaving(false);
    }
  };

  const totalMin = (entries || []).reduce((s, e) => {
    const [fh, fm] = (e.from_time || "0:0").split(":").map(Number);
    const [th, tm] = (e.to_time || "0:0").split(":").map(Number);
    let d = th * 60 + tm - (fh * 60 + fm);
    if (d < 0) d += 1440;
    return s + d;
  }, 0);

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="ts-day-editor">
      <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden max-h-[92dvh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 bg-slate-50">
          <div className="min-w-0">
            <div className="text-[10px] font-black uppercase tracking-widest text-cyan-700">{t("ts.day_edit")}</div>
            <h2 className="text-lg font-black text-slate-900 truncate">{name}</h2>
            <div className="text-xs text-slate-500">{date} · {t("ts.day_total", { h: h1(totalMin) })}</div>
            {dayOt && (dayOt.overtime_minutes || 0) > 0 ? (
              <div className="mt-1 inline-flex items-center gap-1.5 text-[11px] font-black text-amber-600 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5" data-testid="ts-day-ot">
                ⏱ {t("ts.day_ot", { h: h1(dayOt.overtime_minutes) })}
                {(dayOt.ot100_minutes || 0) > 0 ? (
                  <span className="text-amber-400 font-semibold">({t("ts.day_ot_split", { ot50: h1(dayOt.ot50_minutes), ot100: h1(dayOt.ot100_minutes) })})</span>
                ) : null}
              </div>
            ) : null}
            {dayOt && dayOt.break_status === "paid" ? (
              <div className="mt-1 inline-flex items-center gap-1.5 text-[11px] font-black text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5 ml-1" data-testid="ts-day-paid-break"
                title={dayOt.break_code ? `Pauză plătită — cod ${dayOt.break_code}` : "Pauză plătită (nu împinge spre OT)"}>
                ☕ Pauză plătită
              </div>
            ) : null}
          </div>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-900" data-testid="ts-day-editor-close"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-4 space-y-2.5 overflow-y-auto">
          {entries === null ? (
            <div className="py-8 text-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400 inline-block" /></div>
          ) : entries.length === 0 ? (
            <div className="text-center py-6 text-sm text-slate-400 italic">{t("ts.day_none")}</div>
          ) : (
            entries.map((e) => (
              <div key={e.id} className="border border-slate-200 rounded-xl p-3 space-y-2" data-testid={`ts-seg-${e.id}`}>
                <div className="flex items-center gap-2">
                  <input type="time" value={e.from_time} onChange={(ev) => setField(e.id, "from_time", ev.target.value)} className="flex-1 px-2 py-1.5 border border-slate-300 rounded-lg text-sm" data-testid={`ts-seg-from-${e.id}`} />
                  <span className="text-slate-400 text-sm">→</span>
                  <input type="time" value={e.to_time} onChange={(ev) => setField(e.id, "to_time", ev.target.value)} className="flex-1 px-2 py-1.5 border border-slate-300 rounded-lg text-sm" data-testid={`ts-seg-to-${e.id}`} />
                  <button onClick={() => removeSegment(e)} className="w-8 h-8 rounded-lg bg-red-50 hover:bg-red-100 text-red-600 flex items-center justify-center flex-shrink-0" data-testid={`ts-seg-del-${e.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
                <div className="flex items-center gap-2">
                  <select value={e.kind || "work"} onChange={(ev) => setField(e.id, "kind", ev.target.value)} className="px-2 py-1.5 border border-slate-300 rounded-lg text-xs font-semibold" data-testid={`ts-seg-kind-${e.id}`}>
                    <option value="work">{t("ts.kind_work")}</option>
                    <option value="vacation">{t("ts.kind_vacation")}</option>
                    <option value="sick">{t("ts.kind_sick")}</option>
                  </select>
                  {e.project_name ? <span className="text-[11px] text-slate-500 truncate flex-1">{e.project_name}</span> : <span className="text-[11px] text-slate-300 flex-1">{t("ts.no_project")}</span>}
                </div>
                {e.activity_name ? (
                  <div className="flex items-center gap-1.5 text-[11px] text-slate-500" data-testid={`ts-seg-activity-${e.id}`}>
                    <span className="font-black uppercase tracking-wider text-[9px] text-slate-400">{t("ts.seg_activity")}</span>
                    <span className="truncate">{e.activity_name}</span>
                  </div>
                ) : null}
              </div>
            ))
          )}

          <button onClick={addSegment} className="w-full inline-flex items-center justify-center gap-1.5 py-2.5 rounded-xl border-2 border-dashed border-slate-300 text-sm font-bold text-slate-500 hover:border-cyan-400 hover:text-cyan-600" data-testid="ts-seg-add">
            <Plus className="w-4 h-4" /> {t("ts.add_segment")}
          </button>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-200">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-slate-600 hover:bg-slate-100" data-testid="ts-day-editor-cancel">{t("ts.cancel")}</button>
          <button onClick={saveAll} disabled={saving} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-900 hover:bg-slate-800 disabled:opacity-50 text-sm font-semibold text-white" data-testid="ts-day-editor-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {t("ts.save")}
          </button>
        </div>
      </div>
    </div>
  );
}


// -----------------------------------------------------------------------------
// ProjectsView — search a project, see every employee's hours & cost on it.
// -----------------------------------------------------------------------------
function ProjectsView({ month, authHeader, t }) {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    let alive = true;
    setData(null);
    (async () => {
      try {
        const { data: d } = await axios.get(`${TS}/admin/project-hours`, { params: { month }, ...authHeader() });
        if (alive) setData(d);
      } catch {
        if (alive) setData({ projects: [] });
      }
    })();
    return () => { alive = false; };
  }, [month, authHeader]);

  const projects = data?.projects || [];
  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return projects;
    return projects.filter((p) => (p.project_name || "").toLowerCase().includes(term));
  }, [projects, q]);

  const grand = projects.reduce((s, p) => s + p.cost, 0);

  return (
    <>
      <div className="relative mb-3 max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <input
          type="text" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder={t("ts.proj_search")}
          className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-slate-300 text-sm text-slate-900 focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20"
          data-testid="ts-project-search"
        />
      </div>

      {data === null ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-sm text-slate-400 italic bg-white rounded-2xl border border-slate-200">{q ? t("ts.proj_none_found") : t("ts.proj_none")}</div>
      ) : (
        <div className="space-y-2" data-testid="ts-project-list">
          {filtered.map((p) => {
            const open = openId === p.project_id;
            return (
              <div key={p.project_id} className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden" data-testid={`ts-project-${p.project_id}`}>
                <button
                  type="button"
                  onClick={() => setOpenId(open ? null : p.project_id)}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 text-left"
                  data-testid={`ts-project-toggle-${p.project_id}`}
                >
                  <FolderKanban className="w-4 h-4 text-cyan-600 flex-shrink-0" />
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-black text-slate-900 truncate">{p.project_name}</span>
                    <span className="block text-[11px] text-slate-400">{t("ts.proj_employees", { n: p.employees.length })}</span>
                  </span>
                  <span className="text-right flex-shrink-0">
                    <span className="block text-sm font-black text-slate-900">{p.hours}t</span>
                    <span className="block text-[11px] font-bold text-emerald-700">{kr(p.cost)} kr</span>
                  </span>
                  <ChevronDown className={`w-4 h-4 text-slate-400 flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
                </button>
                {open && (
                  <div className="border-t border-slate-100">
                    {p.employees.map((e) => (
                      <div key={e.email} className="flex items-center gap-3 px-4 py-2.5 border-b border-slate-50 last:border-0" data-testid={`ts-project-emp-${p.project_id}-${e.email}`}>
                        <span className="flex-1 min-w-0 text-sm font-semibold text-slate-800 truncate">{e.name}</span>
                        <span className="text-[11px] text-slate-400">{e.days} {e.days === 1 ? t("ts.day_one") : t("ts.day_many")}</span>
                        <span className="text-sm font-black text-slate-900 min-w-[48px] text-right">{e.hours}t</span>
                        <span className="text-sm font-bold text-emerald-700 min-w-[80px] text-right">{kr(e.cost)} kr</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {projects.length > 0 && (
        <div className="mt-3 flex items-center justify-end gap-2 text-sm">
          <span className="text-slate-500">{t("ts.proj_total")}</span>
          <span className="font-black text-emerald-700" data-testid="ts-project-grand-total">{kr(grand)} kr</span>
        </div>
      )}
    </>
  );
}

// -----------------------------------------------------------------------------
// WeekLockBar — manual week locking from the web. A locked week is read-only
// for workers and counts as "approved" for payslips. Auto-lock still runs
// every Monday 12:00; this is the manual override per week.
// -----------------------------------------------------------------------------
function fmtDM(iso) { const [, m, d] = iso.split("-"); return `${d}.${m}`; }

function WeekLockBar({ month, authHeader, t }) {
  const [weeks, setWeeks] = useState(null);
  const [busy, setBusy] = useState("");

  const mondays = useMemo(() => {
    const [y, m] = month.split("-").map(Number);
    const first = new Date(y, m - 1, 1);
    const last = new Date(y, m, 0);
    const monthFirstIso = `${y}-${String(m).padStart(2, "0")}-01`;
    const monthLastIso = `${y}-${String(m).padStart(2, "0")}-${String(last.getDate()).padStart(2, "0")}`;
    const out = [];
    const d = new Date(first);
    d.setDate(d.getDate() - ((d.getDay() + 6) % 7)); // back to Monday
    while (d <= last) {
      const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      const sun = new Date(d); sun.setDate(sun.getDate() + 6);
      const sunIso = `${sun.getFullYear()}-${String(sun.getMonth() + 1).padStart(2, "0")}-${String(sun.getDate()).padStart(2, "0")}`;
      // Last day of THIS week that still belongs to the viewed month → used for
      // partial locking of split weeks (lock only this month's days).
      const inMonthEnd = sunIso > monthLastIso ? monthLastIso : sunIso;
      const isSplit = iso < monthFirstIso || sunIso > monthLastIso;
      out.push({ monday: iso, sunday: sunIso, inMonthEnd, isSplit });
      d.setDate(d.getDate() + 7);
    }
    return out;
  }, [month]);

  const load = useCallback(async () => {
    try {
      const res = await Promise.all(mondays.map((w) =>
        axios.get(`${TS}/lock-status`, { params: { monday: w.monday }, ...authHeader() }).then((r) => r.data).catch(() => null)
      ));
      setWeeks(mondays.map((w, i) => {
        const r = res[i] || {};
        const lt = r.locked_through || null;
        // This month's portion is locked if there's no partial boundary, or the
        // boundary reaches at least the last in-month day of the week.
        const locked = !!r.manual_locked && (!lt || lt >= w.inMonthEnd);
        return { ...w, locked, locked_through: lt };
      }));
    } catch { setWeeks(mondays.map((w) => ({ ...w, locked: false }))); }
  }, [mondays, authHeader]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (w) => {
    setBusy(w.monday);
    const next = !w.locked;
    const body = { week_monday: w.monday, locked: next };
    // Locking a split week only locks the viewed month's days; the rest stays
    // editable for workers. Overtime is still calculated across the full week.
    if (next && w.isSplit) body.through = w.inMonthEnd;
    try {
      await axios.post(`${TS}/admin/lock`, body, authHeader());
      await load();
      toast.success(next ? t("ts.lock_toast_locked", { range: `${fmtDM(w.monday)}–${fmtDM(w.sunday)}` }) : t("ts.lock_toast_unlocked", { range: `${fmtDM(w.monday)}–${fmtDM(w.sunday)}` }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("ts.lock_err"));
    } finally { setBusy(""); }
  };

  return (
    <div className="mb-4 bg-white rounded-2xl border border-slate-200 shadow-sm p-3" data-testid="ts-week-lock-bar">
      <div className="flex items-center gap-2 mb-2">
        <Lock className="w-4 h-4 text-slate-500" />
        <span className="text-[11px] font-black uppercase tracking-widest text-slate-500">{t("ts.lock_title")}</span>
        <span className="text-[11px] text-slate-400 ml-1">{t("ts.lock_hint")}</span>
      </div>
      {weeks === null ? (
        <div className="py-3 flex justify-center"><Loader2 className="w-4 h-4 animate-spin text-slate-400" /></div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {weeks.map((w) => (
            <button
              key={w.monday}
              onClick={() => toggle(w)}
              disabled={busy === w.monday}
              className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold border transition disabled:opacity-50 ${w.locked ? "bg-amber-50 border-amber-300 text-amber-700 hover:bg-amber-100" : "bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100"}`}
              data-testid={`ts-lock-week-${w.monday}`}
            >
              {busy === w.monday ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (w.locked ? <Lock className="w-3.5 h-3.5" /> : <Unlock className="w-3.5 h-3.5" />)}
              {fmtDM(w.monday)}–{fmtDM(w.sunday)}
              {w.isSplit && <span className="text-[8px] uppercase bg-indigo-100 text-indigo-600 px-1 py-0.5 rounded font-black" title={t("ts.lock_split_hint")}>{t("ts.lock_month_only")}</span>}
              <span className={`ml-1 text-[9px] uppercase ${w.locked ? "text-amber-600" : "text-slate-400"}`}>{w.locked ? t("ts.lock_locked") : t("ts.lock_open")}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// MonthTotalsPanel — admin overview of the whole month's gross + company cost.
// -----------------------------------------------------------------------------
function MonthTotalsPanel({ month, authHeader, t }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let alive = true;
    setBusy(true);
    axios.get(`${TS}/admin/payslip-totals`, { params: { month }, ...authHeader() })
      .then(({ data }) => { if (alive) setData(data); })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setBusy(false); });
    return () => { alive = false; };
  }, [month, authHeader]);
  return (
    <div className="mb-4 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="ts-month-totals">
      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="text-[10px] uppercase tracking-widest text-slate-400 font-black">{t("ts.totals_gross")}</div>
        <div className="text-2xl font-black text-slate-900 mt-1" data-testid="ts-total-gross">{busy ? "…" : `kr ${kr(data?.total_gross)}`}</div>
        <div className="text-[11px] text-slate-400 mt-1">{data ? `${data.employees_counted} ${t("ts.totals_emps")}` : ""}</div>
      </div>
      <div className="rounded-2xl border border-indigo-200 bg-indigo-50/60 p-4 shadow-sm">
        <div className="text-[10px] uppercase tracking-widest text-indigo-500 font-black">{t("ts.totals_company")}</div>
        <div className="text-2xl font-black text-indigo-900 mt-1" data-testid="ts-total-company">{busy ? "…" : `kr ${kr(data?.total_company_cost)}`}</div>
        <div className="text-[11px] text-indigo-400 mt-1">{t("ts.totals_company_hint")}</div>
      </div>
    </div>
  );
}


// -----------------------------------------------------------------------------
// -----------------------------------------------------------------------------
// HoursExportModal — one employee's hours over a date range, by day, with
// from/to + project. Export to PDF (print) or CSV.
// -----------------------------------------------------------------------------
function _monthBounds(month) {
  const [y, m] = month.split("-").map(Number);
  const last = new Date(y, m, 0).getDate();
  const p = (n) => String(n).padStart(2, "0");
  return { from: `${y}-${p(m)}-01`, to: `${y}-${p(m)}-${p(last)}` };
}
const _hrs = (h) => `${(h || 0).toFixed(2).replace(".", ",")} t`;
const _dmy = (iso) => { try { const [y, m, d] = iso.split("-"); return `${d}.${m}.${y}`; } catch { return iso; } };

function HoursExportModal({ ctx, month, authHeader, onClose, t }) {
  const init = _monthBounds(month);
  const [from, setFrom] = useState(init.from);
  const [to, setTo] = useState(init.to);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await axios.get(`${TS}/admin/employee-hours`, { params: { email: ctx.email, date_from: from, date_to: to }, ...authHeader() });
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("ts.hours_export_err"));
      setData(null);
    } finally { setBusy(false); }
  };
  useEffect(() => { run(); /* eslint-disable-next-line */ }, []);

  const exportCsv = () => {
    if (!data) return;
    const lines = [["Dato", "Fra", "Til", "Timer", "Prosjekt", "Kommentar"].join(";")];
    data.by_day.forEach((d) => d.rows.forEach((r) => {
      lines.push([_dmy(d.date), r.from_time, r.to_time, (r.hours || 0).toFixed(2).replace(".", ","), `"${(r.project_name || "").replace(/"/g, "'")}"`, `"${(r.comment || "").replace(/"/g, "'")}"`].join(";"));
    }));
    lines.push(["", "", "", (data.total_hours || 0).toFixed(2).replace(".", ","), "TOTAL", ""].join(";"));
    const blob = new Blob(["\uFEFF" + lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `timer_${ctx.name.replace(/\s+/g, "_")}_${from}_${to}.csv`;
    a.click();
  };

  const exportPdf = () => {
    if (!data) return;
    const rowsHtml = data.by_day.map((d) => {
      const inner = d.rows.map((r) => `<tr><td></td><td>${r.from_time}–${r.to_time}</td><td>${(r.hours || 0).toFixed(2).replace(".", ",")} t</td><td>${r.project_name || "—"}</td><td>${r.comment || ""}</td></tr>`).join("");
      return `<tr class="day"><td colspan="2"><b>${_dmy(d.date)}</b></td><td><b>${(d.hours || 0).toFixed(2).replace(".", ",")} t</b></td><td colspan="2"></td></tr>${inner}`;
    }).join("");
    const projHtml = data.by_project.map((p) => `<tr><td>${p.project_name}</td><td style="text-align:right">${(p.hours || 0).toFixed(2).replace(".", ",")} t</td></tr>`).join("");
    const w = window.open("", "_blank");
    w.document.write(`<html><head><title>Timer ${data.name}</title><style>
      body{font-family:Arial,sans-serif;padding:24px;color:#111}
      h1{font-size:18px;margin:0 0 2px}.sub{color:#666;font-size:12px;margin-bottom:16px}
      table{width:100%;border-collapse:collapse;font-size:12px;margin-bottom:18px}
      th,td{border:1px solid #ddd;padding:5px 8px;text-align:left}th{background:#f3f4f6}
      tr.day td{background:#eef6ff}.tot{font-size:14px;font-weight:800;margin:8px 0}
    </style></head><body>
      <h1>Timeliste — ${data.name}</h1>
      <div class="sub">${_dmy(data.date_from)} – ${_dmy(data.date_to)}</div>
      <div class="tot">Total: ${(data.total_hours || 0).toFixed(2).replace(".", ",")} t · ${data.day_count} dager</div>
      <table><thead><tr><th>Dato</th><th>Fra–Til</th><th>Timer</th><th>Prosjekt</th><th>Kommentar</th></tr></thead><tbody>${rowsHtml}</tbody></table>
      <h3 style="font-size:13px">Per prosjekt</h3>
      <table><tbody>${projHtml}</tbody></table>
      <script>window.onload=()=>{window.print()}</script>
    </body></html>`);
    w.document.close();
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="ts-hours-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[88vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
          <div>
            <div className="text-sm font-black text-slate-900 flex items-center gap-2"><Clock className="w-4 h-4 text-cyan-600" /> {t("ts.hours_export_title")}</div>
            <div className="text-xs text-slate-400">{ctx.name}</div>
          </div>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-900" data-testid="ts-hours-close"><X className="w-5 h-5" /></button>
        </div>

        <div className="px-5 py-3 border-b border-slate-100 flex items-end gap-2 flex-wrap">
          <label className="text-xs font-bold text-slate-500">{t("ts.hours_from")}
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="block mt-1 px-2 py-1.5 border border-slate-300 rounded-lg text-sm" data-testid="ts-hours-from" />
          </label>
          <label className="text-xs font-bold text-slate-500">{t("ts.hours_to")}
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="block mt-1 px-2 py-1.5 border border-slate-300 rounded-lg text-sm" data-testid="ts-hours-to" />
          </label>
          <button onClick={run} disabled={busy} className="px-3 py-2 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-sm font-bold disabled:opacity-50" data-testid="ts-hours-run">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : t("ts.hours_show")}
          </button>
          <div className="flex-1" />
          <button onClick={exportPdf} disabled={!data || data.total_minutes === 0} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 text-sm font-bold disabled:opacity-40" data-testid="ts-hours-pdf"><Printer className="w-4 h-4" /> PDF</button>
          <button onClick={exportCsv} disabled={!data || data.total_minutes === 0} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-bold disabled:opacity-40" data-testid="ts-hours-csv"><Download className="w-4 h-4" /> CSV</button>
        </div>

        <div className="overflow-y-auto p-5 space-y-3">
          {busy ? (
            <div className="py-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-cyan-500" /></div>
          ) : !data || data.total_minutes === 0 ? (
            <div className="py-8 text-center text-sm text-slate-400">{t("ts.hours_empty")}</div>
          ) : (
            <>
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-sm"><b className="text-cyan-700">{_hrs(data.total_hours)}</b> <span className="text-slate-400">{t("ts.hours_total")}</span></span>
                <span className="text-xs text-slate-400">· {data.day_count} {t("ts.hours_days")}</span>
              </div>
              {data.by_day.map((d) => (
                <div key={d.date} className="rounded-xl border border-slate-200 overflow-hidden" data-testid={`ts-hours-day-${d.date}`}>
                  <div className="flex items-center justify-between px-3 py-1.5 bg-slate-50 border-b border-slate-100">
                    <span className="text-sm font-black text-slate-700">{_dmy(d.date)}</span>
                    <span className="text-xs font-bold text-cyan-700">{_hrs(d.hours)}</span>
                  </div>
                  <div className="divide-y divide-slate-50">
                    {d.rows.map((r, i) => (
                      <div key={i} className="flex items-center gap-2 px-3 py-1.5 text-sm">
                        <span className="font-mono text-slate-500 w-24 flex-shrink-0">{r.from_time}–{r.to_time}</span>
                        <span className="flex-1 truncate text-slate-700">{r.project_name}</span>
                        <span className="font-bold text-slate-800 w-16 text-right flex-shrink-0">{_hrs(r.hours)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// PayslipModal — Tripletex-style payslip for one employee for the month.
// Renders a print-ready document in a sandboxed iframe (like the diploma
// viewer). Only LOCKED (approved) weeks are counted by the backend.
// -----------------------------------------------------------------------------
const nok = (n) => new Intl.NumberFormat("nb-NO", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

function payslipHtml(p, edit, noteText) {
  const gross = p.gross_amount || 0;
  const skatt = Number(edit?.skattetrekk) || 0;
  const utItems = (edit?.utlegg || []).filter((u) => Number(u.amount) !== 0);
  const utTotal = utItems.reduce((s, u) => s + (Number(u.amount) || 0), 0);
  const ferie = p.feriepenger?.amount || 0;
  const payable = gross + utTotal - skatt;
  const L = p.lines;
  const row = (type, desc, count, rate, amount, neg) => `
    <tr>
      <td class="wt">${type}</td>
      <td class="desc">${desc || ""}</td>
      <td class="num">${count || ""}</td>
      <td class="num">${rate || ""}</td>
      <td class="num amt">${neg && amount > 0 ? "-" + nok(amount) : nok(amount)}</td>
    </tr>`;
  const rows = [row("Timelønn", "", L.timelonn.hours.toFixed(2), nok(L.timelonn.rate), L.timelonn.amount)];
  if (L.overtime_50.hours > 0) rows.push(row("Overtid 50 %", "", L.overtime_50.hours.toFixed(2), nok(L.overtime_50.rate), L.overtime_50.amount));
  if (L.overtime_100.hours > 0) rows.push(row("Overtid 100 %", "", L.overtime_100.hours.toFixed(2), nok(L.overtime_100.rate), L.overtime_100.amount));
  if ((L.sykepenger?.hours || 0) > 0) rows.push(row("Sykepenger (sykdom)", `${L.sykepenger.days} dg`, L.sykepenger.hours.toFixed(2), nok(L.sykepenger.rate), L.sykepenger.amount));
  (p.leader_supplement?.items || []).forEach((s) => rows.push(row("Tillegg bas", s.project_name || "Bas", (s.hours || 0).toFixed(2), nok(s.rate), s.amount)));
  utItems.forEach((u) => rows.push(row("Utlegg", u.description || "Utlegg", "", "", Number(u.amount) || 0)));
  rows.push(row("Skattetrekk", "Forskuddstrekk", "", "", skatt, true));
  const empBank = p.employee.bank_account ? `<br/>Kontonr.: ${p.employee.bank_account}` : "";
  const coBank = p.company.bank_account ? `<br/>Kontonr.: ${p.company.bank_account}` : "";
  return `<!doctype html><html><head><meta charset="utf-8"><style>
    * { box-sizing: border-box; }
    body { font-family: Arial, Helvetica, sans-serif; color: #1f2937; margin: 0; padding: 32px 36px; background: #eceef0; }
    .sheet { background: #eceef0; max-width: 720px; margin: 0 auto; }
    .top { display: flex; justify-content: flex-end; margin-bottom: 40px; }
    .logo { font-size: 26px; font-weight: 800; color: #16a34a; letter-spacing: 1px; }
    .logo small { display:block; font-size: 8px; color:#374151; font-weight:700; letter-spacing:2px; }
    .parties { display: flex; justify-content: space-between; margin-bottom: 48px; font-size: 12px; line-height: 1.5; }
    .parties b { font-size: 13px; }
    .meta { display: flex; justify-content: space-between; margin-bottom: 4px; color:#9ca3af; font-size: 11px; }
    .meta .big { color:#111827; font-size: 20px; font-weight: 700; margin-top:2px; }
    .voucher { color:#9ca3af; font-size: 11px; margin-bottom: 24px; }
    table { width: 100%; border-collapse: collapse; }
    thead th { text-align: left; font-size: 11px; color:#9ca3af; font-weight:600; border-bottom:1px solid #d1d5db; padding: 6px 4px; }
    thead th.num, td.num { text-align: right; }
    tbody td { font-size: 12px; padding: 8px 4px; border-bottom: 1px solid #e5e7eb; }
    td.wt { font-weight: 600; }
    td.amt { font-weight: 600; }
    .total td { font-weight: 800; font-size: 13px; border-top: 2px solid #374151; border-bottom: none; padding-top: 12px; }
    .ferie { margin-top: 16px; font-size: 11px; color:#6b7280; background:#f9fafb; border:1px solid #e5e7eb; border-radius:8px; padding:10px 12px; }
    .ferie b { color:#111827; }
  </style></head><body><div class="sheet">
    <div class="top"><div class="logo">DOSERNE<small>STILLASMONTERING, SALG OG UTLEIE</small></div></div>
    <div class="parties">
      <div><b>${p.employee.name}</b><br/>${(p.employee.address || "").replace(/\n/g, "<br/>")}${empBank}</div>
      <div style="text-align:right"><b>${p.company.name}</b><br/>${p.company.org_no}<br/>${p.company.address}<br/>Phone no.: ${p.company.phone}<br/>Email: ${p.company.email}${coBank}</div>
    </div>
    <div class="meta"><span>Payslip for</span><span>Payment date</span><span>Payable amount</span></div>
    <div class="meta"><span class="big">${p.payslip_for}</span><span class="big">${p.payment_date}</span><span class="big">${nok(payable)}</span></div>
    <div class="voucher">Salary voucher: ${p.voucher_number} &nbsp;&nbsp; National identity number: ${p.employee.personnummer || "—"}</div>
    <table>
      <thead><tr><th>Wage type</th><th>Description</th><th class="num">Count</th><th class="num">Rate</th><th class="num">Amount</th></tr></thead>
      <tbody>
        ${rows.join("")}
        <tr class="total"><td colspan="4">Netto til utbetaling</td><td class="num amt">${nok(payable)}</td></tr>
      </tbody>
    </table>
    <div class="ferie">Feriepenger opptjent (${Math.round((p.feriepenger?.rate || 0.12) * 100)} %): <b>${nok(ferie)}</b> — utbetales i juni neste år (ikke inkludert i beløpet over).</div>
    <p style="font-size:10px;color:#9ca3af;margin-top:16px">${noteText || ""}</p>
  </div></body></html>`;
}

function PayslipModal({ ctx, month, authHeader, onClose, t }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [skattetrekk, setSkattetrekk] = useState("0");
  const [utlegg, setUtlegg] = useState([]); // [{description, amount}]
  const [companyBank, setCompanyBank] = useState("");
  const [saving, setSaving] = useState(false);
  const iframeRef = React.useRef(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data: d } = await axios.get(`${TS}/admin/payslip`, { params: { email: ctx.email, month }, ...authHeader() });
        if (!alive) return;
        setData(d);
        setSkattetrekk(String(d.skattetrekk || 0));
        setUtlegg((d.utlegg?.items || []).map((u) => ({ description: u.description || "", amount: String(u.amount || 0) })));
        setCompanyBank(d.company?.bank_account || "");
      } catch (e) {
        if (alive) setErr(e?.response?.data?.detail || t("ts.payslip_err"));
      }
    })();
    return () => { alive = false; };
  }, [ctx.email, month, authHeader, t]);

  const edit = useMemo(() => ({
    skattetrekk: Number(skattetrekk) || 0,
    utlegg: utlegg.map((u) => ({ description: u.description, amount: Number(u.amount) || 0 })),
  }), [skattetrekk, utlegg]);
  const html = useMemo(
    () => (data ? payslipHtml({ ...data, company: { ...data.company, bank_account: companyBank } }, edit, t("ts.payslip_note", { n: data.locked_weeks })) : ""),
    [data, edit, companyBank, t],
  );
  const doPrint = () => { const w = iframeRef.current?.contentWindow; if (w) { w.focus(); w.print(); } };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${TS}/admin/payslip/extras`, {
        skattetrekk: Number(skattetrekk) || 0,
        utlegg: utlegg.map((u) => ({ description: u.description, amount: Number(u.amount) || 0 })),
        company_bank_account: companyBank,
      }, { params: { email: ctx.email, month }, ...authHeader() });
      toast.success(t("ts.payslip_saved"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("ts.payslip_err"));
    }
    setSaving(false);
  };

  const addUtlegg = () => setUtlegg((prev) => [...prev, { description: "", amount: "" }]);
  const setUt = (i, k, v) => setUtlegg((prev) => prev.map((u, idx) => (idx === i ? { ...u, [k]: v } : u)));
  const delUt = (i) => setUtlegg((prev) => prev.filter((_, idx) => idx !== i));

  // Close via browser Back: push a history entry when the payslip opens so the
  // browser Back button (and Escape) just closes this pop-out instead of
  // navigating away from the timesheet page.
  const onCloseRef = React.useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    window.history.pushState({ dPayslip: true }, "");
    const onPop = () => onCloseRef.current();
    const onKey = (e) => { if (e.key === "Escape") window.history.back(); };
    window.addEventListener("popstate", onPop);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("popstate", onPop);
      window.removeEventListener("keydown", onKey);
    };
  }, []);
  const requestClose = () => window.history.back();

  return (
    <div
      className="fixed inset-0 z-[110] bg-black/70 flex items-start sm:items-center justify-center p-2 sm:p-4 overflow-y-auto"
      data-testid="ts-payslip-modal"
      onMouseDown={(e) => { if (e.target === e.currentTarget) requestClose(); }}
    >
      <div className="w-full max-w-[840px] my-auto bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[94vh]">
        <div className="flex items-center justify-between px-4 py-3 bg-slate-900 text-white flex-shrink-0">
          <div className="min-w-0">
            <div className="text-[10px] font-black uppercase tracking-widest text-indigo-300">{t("ts.payslip_title")}</div>
            <div className="font-black truncate">{ctx.name} · {data?.payslip_for || monthLabel(month, t)}</div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={save} disabled={!data || !data.has_salary || saving} className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm font-bold px-3 py-2 rounded-lg" data-testid="ts-payslip-save">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {t("ts.payslip_save")}
            </button>
            <button onClick={doPrint} disabled={!data} className="inline-flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-sm font-bold px-3 py-2 rounded-lg" data-testid="ts-payslip-print"><Printer className="w-4 h-4" /> {t("ts.payslip_print")}</button>
            <button onClick={requestClose} className="w-9 h-9 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center" data-testid="ts-payslip-close"><X className="w-5 h-5" /></button>
          </div>
        </div>

        {data && data.has_salary && (
          <div className="bg-slate-800 text-white px-4 py-3 flex flex-wrap items-end gap-4 flex-shrink-0">
            <label className="flex flex-col gap-1">
              <span className="text-[10px] uppercase tracking-widest text-slate-400 font-black">{t("ts.payslip_tax")}</span>
              <input type="number" min="0" value={skattetrekk} onChange={(e) => setSkattetrekk(e.target.value)} className="w-32 bg-slate-700 rounded-lg px-2 py-1.5 text-sm text-right outline-none focus:ring-2 focus:ring-indigo-400" data-testid="ts-payslip-tax" />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-[10px] uppercase tracking-widest text-slate-400 font-black">{t("ts.payslip_company_bank")}</span>
              <input value={companyBank} onChange={(e) => setCompanyBank(e.target.value)} placeholder="0000.00.00000" className="w-40 bg-slate-700 rounded-lg px-2 py-1.5 text-sm outline-none focus:ring-2 focus:ring-indigo-400" data-testid="ts-payslip-company-bank" />
            </label>
            <div className="flex-1 min-w-[240px]">
              <div className="text-[10px] uppercase tracking-widest text-slate-400 font-black mb-1">Utlegg</div>
              <div className="space-y-1.5">
                {utlegg.map((u, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input value={u.description} onChange={(e) => setUt(i, "description", e.target.value)} placeholder={t("ts.payslip_utlegg_desc")} className="flex-1 bg-slate-700 rounded-lg px-2 py-1.5 text-sm outline-none focus:ring-2 focus:ring-indigo-400" data-testid={`ts-utlegg-desc-${i}`} />
                    <input type="number" value={u.amount} onChange={(e) => setUt(i, "amount", e.target.value)} placeholder="kr" className="w-24 bg-slate-700 rounded-lg px-2 py-1.5 text-sm text-right outline-none focus:ring-2 focus:ring-indigo-400" data-testid={`ts-utlegg-amt-${i}`} />
                    <button onClick={() => delUt(i)} className="w-8 h-8 rounded-lg bg-red-500/20 hover:bg-red-500/40 text-red-300 flex items-center justify-center" data-testid={`ts-utlegg-del-${i}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
                <button onClick={addUtlegg} className="inline-flex items-center gap-1.5 text-xs font-bold text-indigo-300 hover:text-indigo-200" data-testid="ts-utlegg-add"><Plus className="w-3.5 h-3.5" /> {t("ts.payslip_utlegg_add")}</button>
              </div>
            </div>
          </div>
        )}

        {data && data.has_salary && data.unlocked_weeks_with_hours > 0 && (
          <div className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white text-xs font-bold flex-shrink-0" data-testid="ts-payslip-warn">
            <span className="text-base leading-none">⚠</span>
            {t("ts.payslip_warn", { weeks: data.unlocked_weeks_with_hours, hours: (Math.round(((data.excluded?.normal_hours || 0) + (data.excluded?.ot50_hours || 0) + (data.excluded?.ot100_hours || 0)) * 10) / 10) })}
          </div>
        )}
        <div className="flex-1 min-h-0 overflow-auto p-3 bg-slate-200">
          {err ? (
            <div className="h-full flex items-center justify-center text-sm text-red-600 bg-white rounded">{err}</div>
          ) : !data ? (
            <div className="h-full flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-500" /></div>
          ) : !data.has_salary ? (
            <div className="h-full flex flex-col items-center justify-center gap-2 text-center bg-white rounded p-10">
              <Receipt className="w-8 h-8 text-slate-300" />
              <div className="text-sm text-slate-600 font-semibold">{t("ts.payslip_no_salary")}</div>
              <div className="text-xs text-slate-400">{t("ts.payslip_no_salary_hint")}</div>
            </div>
          ) : (
            <iframe ref={iframeRef} title="Payslip" srcDoc={html} className="w-full bg-white rounded shadow-lg mx-auto block" style={{ aspectRatio: "1 / 1.414" }} data-testid="ts-payslip-frame" />
          )}
        </div>
      </div>
    </div>
  );
}


// -----------------------------------------------------------------------------
// ReviewView — pick an employee OR a project, open the whole month's hours with
// exact from→to times per day. Read-only review (click a row to edit via the
// day editor is out of scope here; use the Employees grid for edits).
// -----------------------------------------------------------------------------
function ReviewView({ month, authHeader, t, members }) {
  const [mode, setMode] = useState("employee"); // employee | project
  const [empEmail, setEmpEmail] = useState("");
  const [projId, setProjId] = useState("");
  const [projects, setProjects] = useState([]);
  const [entries, setEntries] = useState(null); // null = not opened yet
  const [busy, setBusy] = useState(false);

  // Load the project list (for the project selector).
  useEffect(() => {
    let alive = true;
    axios.get(`${TS}/admin/project-hours`, { params: { month }, ...authHeader() })
      .then((r) => { if (alive) setProjects(r.data.projects || []); })
      .catch(() => { if (alive) setProjects([]); });
    return () => { alive = false; };
  }, [month, authHeader]);

  const open = useCallback(async () => {
    const params = { month };
    if (mode === "employee") { if (!empEmail) return; params.email = empEmail; }
    else { if (!projId) return; params.project_id = projId; }
    setBusy(true);
    try {
      const { data } = await axios.get(`${TS}/admin/review`, { params, ...authHeader() });
      setEntries(data.entries || []);
    } catch { setEntries([]); }
    finally { setBusy(false); }
  }, [mode, empEmail, projId, month, authHeader]);

  // Re-open automatically when month changes if a selection is active.
  useEffect(() => { if (entries !== null) open(); /* eslint-disable-next-line */ }, [month]);

  const WDL = (t("ts.weekdays") || "").split(",");
  const grouped = useMemo(() => {
    const g = {};
    (entries || []).forEach((e) => { (g[e.date] = g[e.date] || []).push(e); });
    return Object.keys(g).sort().map((d) => ({ date: d, items: g[d] }));
  }, [entries]);
  const grandMin = (entries || []).filter((e) => !e.is_break).reduce((s, e) => s + (e.minutes || 0), 0);

  const kindLabel = (k) => k === "vacation" ? t("ts.kind_vacation") : k === "sick" ? t("ts.kind_sick") : t("ts.kind_work");

  return (
    <div data-testid="ts-review">
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 mb-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-1">
            <button onClick={() => { setMode("employee"); setEntries(null); }} className={`px-3 py-1.5 rounded-md text-xs font-bold ${mode === "employee" ? "bg-slate-900 text-white" : "text-slate-500"}`} data-testid="ts-review-mode-employee">{t("ts.review_by_employee")}</button>
            <button onClick={() => { setMode("project"); setEntries(null); }} className={`px-3 py-1.5 rounded-md text-xs font-bold ${mode === "project" ? "bg-slate-900 text-white" : "text-slate-500"}`} data-testid="ts-review-mode-project">{t("ts.review_by_project")}</button>
          </div>
          {mode === "employee" ? (
            <select value={empEmail} onChange={(e) => { setEmpEmail(e.target.value); setEntries(null); }} className="flex-1 min-w-[200px] px-3 py-2 rounded-lg border border-slate-300 text-sm font-semibold" data-testid="ts-review-employee-select">
              <option value="">{t("ts.review_choose_employee")}</option>
              {(members || []).map((m) => <option key={m.email} value={m.email}>{m.name}</option>)}
            </select>
          ) : (
            <select value={projId} onChange={(e) => { setProjId(e.target.value); setEntries(null); }} className="flex-1 min-w-[200px] px-3 py-2 rounded-lg border border-slate-300 text-sm font-semibold" data-testid="ts-review-project-select">
              <option value="">{t("ts.review_choose_project")}</option>
              {projects.map((p) => <option key={p.project_id} value={p.project_id}>{p.project_name}</option>)}
            </select>
          )}
          <button onClick={open} disabled={busy || (mode === "employee" ? !empEmail : !projId)} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-cyan-600 hover:bg-cyan-700 disabled:opacity-40 text-white text-sm font-bold" data-testid="ts-review-open">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalIcon className="w-4 h-4" />} {t("ts.review_open")}
          </button>
        </div>
      </div>

      {entries === null ? (
        <div className="text-center py-16 text-sm text-slate-400 italic bg-white rounded-2xl border border-slate-200">{t("ts.review_pick_first")}</div>
      ) : entries.length === 0 ? (
        <div className="text-center py-16 text-sm text-slate-400 italic bg-white rounded-2xl border border-slate-200">{t("ts.review_empty")}</div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden" data-testid="ts-review-list">
          {grouped.map((g) => {
            const dObj = new Date(g.date);
            const wd = (dObj.getDay() + 6) % 7; // 0=Mon
            const dayMin = g.items.filter((e) => !e.is_break).reduce((s, e) => s + (e.minutes || 0), 0);
            return (
              <div key={g.date} className="border-b border-slate-100 last:border-0" data-testid={`ts-review-day-${g.date}`}>
                <div className="flex items-center justify-between px-4 py-2 bg-slate-50">
                  <span className="text-sm font-black text-slate-800">{WDL[wd]} {g.date.slice(8)}.{g.date.slice(5, 7)}</span>
                  <span className="text-xs font-bold text-slate-500">{h1(dayMin)}t</span>
                </div>
                {g.items.map((e) => (
                  <div key={e.id} className="flex items-center gap-3 px-4 py-2 border-t border-slate-50 first:border-0" data-testid={`ts-review-entry-${e.id}`}>
                    <span className="font-mono text-sm font-black text-slate-900 tabular-nums">{e.from_time} <span className="text-slate-300">→</span> {e.to_time}</span>
                    <span className={`text-[10px] font-black uppercase px-1.5 py-0.5 rounded ${e.kind === "vacation" ? "bg-sky-100 text-sky-700" : e.kind === "sick" ? "bg-violet-100 text-violet-700" : e.is_break ? "bg-slate-100 text-slate-400" : "bg-emerald-100 text-emerald-700"}`}>{e.is_break ? t("ts.review_break") : kindLabel(e.kind)}</span>
                    <span className="flex-1 min-w-0 text-xs text-slate-500 truncate">{mode === "employee" ? (e.project_name || t("ts.no_project")) : e.user_name}</span>
                    {!e.is_break && <span className="text-xs font-bold text-slate-600">{h1(e.minutes)}t</span>}
                  </div>
                ))}
              </div>
            );
          })}
          <div className="flex items-center justify-end gap-2 px-4 py-3 bg-slate-50 border-t border-slate-200">
            <span className="text-sm text-slate-500">Total:</span>
            <span className="text-sm font-black text-slate-900" data-testid="ts-review-total">{h1(grandMin)}t</span>
          </div>
        </div>
      )}
    </div>
  );
}


// -----------------------------------------------------------------------------
// TripletexImportButton — pull the month's hours from Tripletex (overwrite),
// then show a result summary (matched / created / unmatched employees).
// -----------------------------------------------------------------------------
function TripletexImportButton({ month, authHeader, t, onDone }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    if (!window.confirm(t("ts.import_confirm"))) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/tripletex/import-hours`, { month }, authHeader());
      setResult(data);
      toast.success(`${t("ts.import_matched")}: ${data.matched_employees} · ${t("ts.import_entries")}: ${data.entries_created}`);
      onDone && onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("ts.import_err"));
    } finally { setBusy(false); }
  };

  return (
    <>
      <button
        onClick={run}
        disabled={busy}
        className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl border border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 disabled:opacity-50 text-sm font-bold shadow-sm"
        data-testid="ts-tlx-import"
        title="Tripletex"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Coins className="w-4 h-4" />}
        {busy ? t("ts.import_running") : t("ts.import_btn")}
      </button>

      {result && (
        <div className="fixed inset-0 z-[120] bg-black/60 flex items-center justify-center p-4" onClick={() => setResult(null)} data-testid="ts-import-result">
          <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden max-h-[90dvh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 bg-slate-900 text-white">
              <div className="font-black">{t("ts.import_result_title")}</div>
              <button onClick={() => setResult(null)} className="p-1 text-slate-300 hover:text-white" data-testid="ts-import-result-close"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-3 overflow-y-auto">
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="bg-emerald-50 rounded-xl p-3"><div className="text-2xl font-black text-emerald-700">{result.matched_employees}</div><div className="text-[10px] font-bold uppercase text-emerald-600">{t("ts.import_matched")}</div></div>
                <div className="bg-cyan-50 rounded-xl p-3"><div className="text-2xl font-black text-cyan-700">{result.entries_created}</div><div className="text-[10px] font-bold uppercase text-cyan-600">{t("ts.import_entries")}</div></div>
                <div className="bg-slate-100 rounded-xl p-3"><div className="text-2xl font-black text-slate-700">{result.projects_mapped}</div><div className="text-[10px] font-bold uppercase text-slate-500">{t("ts.import_projects")}</div></div>
              </div>
              <div>
                <div className="text-xs font-black uppercase tracking-wider text-slate-500 mb-1.5">{t("ts.import_unmatched")} ({(result.unmatched_names || []).length})</div>
                {(result.unmatched_names || []).length === 0 ? (
                  <div className="text-sm text-emerald-600 font-semibold">{t("ts.import_none_unmatched")}</div>
                ) : (
                  <div className="flex flex-wrap gap-1.5 max-h-52 overflow-y-auto">
                    {result.unmatched_names.map((n) => (
                      <span key={n} className="text-[11px] font-semibold bg-amber-50 border border-amber-200 text-amber-700 rounded-lg px-2 py-1">{n}</span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}


// -----------------------------------------------------------------------------
// SyncHistory — compact panel showing the last Tripletex hours sync (auto or
// manual) with imported entries / vacation / sick counts. Auto-sync runs daily
// at 03:00 Oslo; this reads GET /tripletex/sync-log.
// -----------------------------------------------------------------------------
function SyncHistory({ authHeader, t, refreshKey }) {
  const [runs, setRuns] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/tripletex/sync-log`, { params: { limit: 5 }, ...authHeader() });
        if (alive) setRuns(data.runs || []);
      } catch { if (alive) setRuns([]); }
    })();
    return () => { alive = false; };
  }, [authHeader, refreshKey]);

  if (runs === null) return null;

  const fmt = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleString("no-NO", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
    } catch { return iso; }
  };
  const last = runs[0];

  return (
    <div className="mb-4 rounded-xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-3 shadow-sm" data-testid="ts-sync-history">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="inline-flex items-center gap-1.5 text-[11px] font-black uppercase tracking-wider text-slate-500">
          <Coins className="w-3.5 h-3.5 text-indigo-500" /> {t("ts.sync_history")}
        </div>
        <div className="text-[11px] font-semibold text-slate-400">{t("ts.sync_auto_hint")}</div>
      </div>

      {!last ? (
        <div className="mt-2 text-sm text-slate-400 font-semibold" data-testid="ts-sync-empty">{t("ts.sync_never")}</div>
      ) : (
        <div className="mt-2 space-y-1.5">
          {runs.map((r, i) => (
            <div
              key={r.id || i}
              className={`flex items-center justify-between gap-2 flex-wrap rounded-lg px-2.5 py-1.5 ${i === 0 ? "bg-indigo-50 border border-indigo-100" : ""}`}
              data-testid={`ts-sync-row-${i}`}
            >
              <div className="flex items-center gap-2">
                <span className={`text-[10px] font-black uppercase px-1.5 py-0.5 rounded ${r.trigger === "auto" ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`}>
                  {r.trigger === "auto" ? t("ts.sync_auto") : t("ts.sync_manual")}
                </span>
                <span className="text-xs font-bold text-slate-700">{fmt(r.created_at)}</span>
                <span className="text-[11px] text-slate-400 font-semibold">{r.month}</span>
              </div>
              <div className="flex items-center gap-3 text-xs font-bold">
                <span className="text-cyan-700">{r.entries_created} {t("ts.import_entries")}</span>
                <span className="text-amber-600">{r.vacation_days} {t("ts.sync_vacation")}</span>
                <span className="text-rose-600">{r.sick_days} {t("ts.sync_sick")}</span>
                <span className="text-emerald-700">{r.matched_employees}👤</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// -----------------------------------------------------------------------------
// LinkEmployeesButton — manual matching of Tripletex employees to /doserne
// users. Fixes people whose Tripletex name is spelled differently (extra
// middle name, typo). Links are stored server-side and reused on every import
// incl. the daily auto-sync. GET /tripletex/employees, POST/DELETE emp-map.
// -----------------------------------------------------------------------------
// -----------------------------------------------------------------------------
// BackfillButton — one-shot import of Tripletex hours for the whole year so far
// (1 Jan → today). Runs in the background; polls status and shows progress.
// -----------------------------------------------------------------------------
function BackfillButton({ authHeader, t, onDone }) {
  const [state, setState] = useState(null); // {running, done, total, current}

  useEffect(() => {
    let alive = true;
    let timer;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/tripletex/import-hours-range/status`, authHeader());
        if (!alive) return;
        setState(data);
        if (data.running) { timer = setTimeout(poll, 2500); }
        else if (state?.running) { onDone?.(); }
      } catch { /* silent */ }
    };
    poll();
    return () => { alive = false; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authHeader]);

  const start = async () => {
    if (!window.confirm(t("ts.backfill_confirm"))) return;
    try {
      await axios.post(`${API}/tripletex/import-hours-range`, {}, authHeader());
      toast.success(t("ts.backfill_started"));
      setState({ running: true, done: [], total: 0, current: null });
      const tick = async () => {
        const { data } = await axios.get(`${API}/tripletex/import-hours-range/status`, authHeader());
        setState(data);
        if (data.running) setTimeout(tick, 2500); else onDone?.();
      };
      setTimeout(tick, 2000);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const running = state?.running;
  const label = running
    ? `${t("ts.backfill_running")} ${(state.done?.length || 0)}/${state.total || "…"}`
    : t("ts.backfill_btn");

  return (
    <button
      onClick={start}
      disabled={running}
      className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 text-sm font-bold shadow-sm disabled:opacity-60"
      data-testid="ts-backfill-btn"
    >
      {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} {label}
    </button>
  );
}


function LinkEmployeesButton({ authHeader, t }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null); // { members, employees }
  const [pick, setPick] = useState({}); // tlxId -> selected email
  const [savingId, setSavingId] = useState(null);
  const [syncBusy, setSyncBusy] = useState(false);
  const [syncPreview, setSyncPreview] = useState(null); // null | { count, renames }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/tripletex/employees`, authHeader());
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("ts.link_err"));
      setData({ members: [], employees: [] });
    } finally { setLoading(false); }
  }, [authHeader, t]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const link = async (tlxId) => {
    const email = pick[tlxId];
    if (!email) return;
    setSavingId(tlxId);
    try {
      await axios.post(`${API}/tripletex/emp-map`, { tripletex_employee_id: tlxId, user_email: email }, authHeader());
      toast.success(t("ts.link_saved"));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("ts.link_err"));
    } finally { setSavingId(null); }
  };

  const unlink = async (tlxId) => {
    setSavingId(tlxId);
    try {
      await axios.delete(`${API}/tripletex/emp-map/${tlxId}`, authHeader());
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("ts.link_err"));
    } finally { setSavingId(null); }
  };

  // Preview which /doserne members would be renamed to match Tripletex exactly.
  const previewSync = async () => {
    setSyncBusy(true);
    try {
      const { data } = await axios.post(`${API}/tripletex/sync-employee-names`, { dry_run: true }, authHeader());
      setSyncPreview({ count: data.count, renames: data.renames || [] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("ts.sync_names_err"));
    } finally { setSyncBusy(false); }
  };

  // Apply the renames (updates member names + Underlag autocomplete list).
  const applySync = async () => {
    setSyncBusy(true);
    try {
      const { data } = await axios.post(`${API}/tripletex/sync-employee-names`, { dry_run: false }, authHeader());
      toast.success(t("ts.sync_names_applied").replace("{n}", data.applied));
      setSyncPreview(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("ts.sync_names_err"));
    } finally { setSyncBusy(false); }
  };

  const employees = data?.employees || [];
  const members = data?.members || [];
  const unmatched = employees.filter((e) => !e.matched);
  const matched = employees.filter((e) => e.matched);

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 text-sm font-bold shadow-sm"
        data-testid="ts-link-employees"
      >
        <Link2 className="w-4 h-4" /> {t("ts.link_btn")}
      </button>

      {open && (
        <div className="fixed inset-0 z-[120] bg-black/60 flex items-center justify-center p-4" onClick={() => setOpen(false)} data-testid="ts-link-modal">
          <div className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl overflow-hidden max-h-[90dvh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 bg-slate-900 text-white shrink-0">
              <div>
                <div className="font-black">{t("ts.link_title")}</div>
                <div className="text-[11px] text-slate-300 font-medium mt-0.5 max-w-md">{t("ts.link_desc")}</div>
              </div>
              <button onClick={() => setOpen(false)} className="p-1 text-slate-300 hover:text-white" data-testid="ts-link-close"><X className="w-5 h-5" /></button>
            </div>

            <div className="p-5 overflow-y-auto space-y-4">
              {loading ? (
                <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
              ) : (
                <>
                  {/* Sync names to Tripletex (exact spelling) */}
                  <div className="rounded-xl border border-indigo-200 bg-indigo-50/60 p-3" data-testid="ts-sync-names-box">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="min-w-0 flex-1">
                        <div className="text-xs font-black uppercase tracking-wider text-indigo-700">{t("ts.sync_names_title")}</div>
                        <div className="text-[11px] text-slate-500 font-medium mt-0.5">{t("ts.sync_names_desc")}</div>
                      </div>
                      <button
                        onClick={previewSync}
                        disabled={syncBusy}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-indigo-300 bg-white text-indigo-700 hover:bg-indigo-100 text-sm font-bold disabled:opacity-50"
                        data-testid="ts-sync-names-preview"
                      >
                        {syncBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} {t("ts.sync_names_preview")}
                      </button>
                    </div>
                    {syncPreview && (
                      <div className="mt-3" data-testid="ts-sync-names-result">
                        {syncPreview.count === 0 ? (
                          <div className="text-sm text-emerald-600 font-semibold">{t("ts.sync_names_none")}</div>
                        ) : (
                          <>
                            <div className="space-y-1 max-h-48 overflow-y-auto mb-2">
                              {syncPreview.renames.map((r) => (
                                <div key={r.email} className="flex items-center gap-2 text-sm bg-white rounded-lg px-2.5 py-1.5 border border-slate-100">
                                  <span className="text-slate-400 line-through">{r.old_name}</span>
                                  <span className="text-slate-400">→</span>
                                  <span className="font-bold text-slate-800">{r.new_name}</span>
                                </div>
                              ))}
                            </div>
                            <button
                              onClick={applySync}
                              disabled={syncBusy}
                              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-indigo-600 text-white text-sm font-black hover:bg-indigo-700 disabled:opacity-50"
                              data-testid="ts-sync-names-apply"
                            >
                              {syncBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} {t("ts.sync_names_apply").replace("{n}", syncPreview.count)}
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Unmatched */}
                  <div>
                    <div className="text-xs font-black uppercase tracking-wider text-amber-600 mb-2">
                      {t("ts.link_unmatched")} ({unmatched.length})
                    </div>
                    {unmatched.length === 0 ? (
                      <div className="text-sm text-emerald-600 font-semibold" data-testid="ts-link-none">{t("ts.link_none_unmatched")}</div>
                    ) : (
                      <div className="space-y-2">
                        {unmatched.map((e) => (
                          <div key={e.tripletex_employee_id} className="flex items-center gap-2 flex-wrap bg-amber-50 border border-amber-200 rounded-xl px-3 py-2" data-testid={`ts-link-row-${e.tripletex_employee_id}`}>
                            <div className="font-bold text-sm text-slate-800 min-w-[160px] flex-1">{e.name}</div>
                            <select
                              value={pick[e.tripletex_employee_id] || ""}
                              onChange={(ev) => setPick((p) => ({ ...p, [e.tripletex_employee_id]: ev.target.value }))}
                              className="text-sm border border-slate-300 rounded-lg px-2 py-1.5 bg-white max-w-[220px]"
                              data-testid={`ts-link-select-${e.tripletex_employee_id}`}
                            >
                              <option value="">{t("ts.link_choose")}</option>
                              {members.map((m) => (
                                <option key={m.email} value={m.email}>{m.name}</option>
                              ))}
                            </select>
                            <button
                              onClick={() => link(e.tripletex_employee_id)}
                              disabled={!pick[e.tripletex_employee_id] || savingId === e.tripletex_employee_id}
                              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 disabled:opacity-40"
                              data-testid={`ts-link-save-${e.tripletex_employee_id}`}
                            >
                              {savingId === e.tripletex_employee_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />} {t("ts.link_save")}
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Matched */}
                  {matched.length > 0 && (
                    <div>
                      <div className="text-xs font-black uppercase tracking-wider text-emerald-600 mb-2">
                        {t("ts.link_matched")} ({matched.length})
                      </div>
                      <div className="space-y-1.5 max-h-60 overflow-y-auto">
                        {matched.map((e) => (
                          <div key={e.tripletex_employee_id} className="flex items-center gap-2 flex-wrap bg-slate-50 border border-slate-100 rounded-lg px-3 py-1.5 text-sm">
                            <div className="font-bold text-slate-700 flex-1 min-w-[140px]">{e.name}</div>
                            <span className="text-[10px] font-black uppercase px-1.5 py-0.5 rounded bg-slate-200 text-slate-500">{t(`ts.link_by_${e.match_by}`)}</span>
                            <span className="text-xs text-slate-500 font-semibold">→ {e.matched_name || e.matched_email}</span>
                            {e.match_by === "manual" && (
                              <button
                                onClick={() => unlink(e.tripletex_employee_id)}
                                disabled={savingId === e.tripletex_employee_id}
                                className="text-[11px] font-bold text-rose-600 hover:text-rose-700 disabled:opacity-40"
                                data-testid={`ts-unlink-${e.tripletex_employee_id}`}
                              >
                                {t("ts.link_unlink")}
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}



// -----------------------------------------------------------------------------
// BreakCodeCreate — admin generates a paid-break code from the web (project +
// optional label + validity window). Mirrors the mobile project-screen flow.
function BreakCodeCreate({ authHeader, month }) {
  const [open, setOpen] = useState(false);
  const [projects, setProjects] = useState([]);
  const [projId, setProjId] = useState("");
  const [labelTxt, setLabelTxt] = useState("");
  const [validFrom, setValidFrom] = useState("");
  const [validTo, setValidTo] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [created, setCreated] = useState(null);
  const [codes, setCodes] = useState([]);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let alive = true;
    axios.get(`${TS}/admin/project-hours`, { params: { month }, ...authHeader() })
      .then((r) => { if (alive) setProjects(r.data.projects || []); })
      .catch(() => { if (alive) setProjects([]); });
    return () => { alive = false; };
  }, [month, authHeader]);

  useEffect(() => {
    let alive = true;
    axios.get(`${TS}/admin/break-codes`, authHeader())
      .then((r) => { if (alive) setCodes(r.data.codes || []); })
      .catch(() => { if (alive) setCodes([]); });
    return () => { alive = false; };
  }, [authHeader, tick]);

  const submit = async () => {
    setErr(""); setCreated(null);
    if (!projId) { setErr("Velg et prosjekt."); return; }
    if (validFrom && validTo && validFrom > validTo) { setErr("Gyldig fra må være før gyldig til."); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${TS}/admin/break-codes`,
        { project_id: projId, label: labelTxt.trim(), valid_from: validFrom, valid_to: validTo },
        authHeader());
      setCreated(data);
      setLabelTxt(""); setValidFrom(""); setValidTo("");
      setTick((x) => x + 1);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Kunne ikke opprette koden.");
    }
    setBusy(false);
  };

  return (
    <div className="mb-4 rounded-xl border border-slate-200 bg-white p-3 shadow-sm" data-testid="ts-bccreate">
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between gap-2" data-testid="ts-bccreate-toggle">
        <div className="inline-flex items-center gap-1.5 text-[11px] font-black uppercase tracking-wider text-slate-500">
          <Coins className="w-3.5 h-3.5 text-emerald-500" /> Opprett pausekode
        </div>
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open ? (
        <div className="mt-3 space-y-2.5">
          <div>
            <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1">Prosjekt</label>
            <select value={projId} onChange={(e) => setProjId(e.target.value)} className="w-full text-sm font-semibold border border-slate-200 rounded-lg px-2 py-2 bg-white" data-testid="ts-bccreate-project">
              <option value="">— Velg prosjekt —</option>
              {projects.map((p) => <option key={p.project_id} value={p.project_id}>{p.project_name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1">Merkelapp <span className="normal-case tracking-normal text-slate-300">(valgfritt, f.eks. «Dekket av kunde»)</span></label>
            <input value={labelTxt} onChange={(e) => setLabelTxt(e.target.value)} className="w-full text-sm border border-slate-200 rounded-lg px-2 py-2" placeholder="Dekket av kunde" data-testid="ts-bccreate-label" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1">Gyldig fra</label>
              <input type="date" value={validFrom} onChange={(e) => setValidFrom(e.target.value)} className="w-full text-sm border border-slate-200 rounded-lg px-2 py-2" data-testid="ts-bccreate-from" />
            </div>
            <div>
              <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1">Gyldig til</label>
              <input type="date" value={validTo} onChange={(e) => setValidTo(e.target.value)} className="w-full text-sm border border-slate-200 rounded-lg px-2 py-2" data-testid="ts-bccreate-to" />
            </div>
          </div>
          {err ? <div className="text-xs font-bold text-red-600" data-testid="ts-bccreate-err">{err}</div> : null}
          <button onClick={submit} disabled={busy} className="w-full inline-flex items-center justify-center gap-1.5 text-sm font-black text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 rounded-lg py-2.5" data-testid="ts-bccreate-submit">
            {busy ? "Oppretter…" : "Opprett kode"}
          </button>
          {created ? (
            <div className="mt-1 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-center" data-testid="ts-bccreate-result">
              <div className="text-[10px] font-black uppercase tracking-widest text-emerald-600 mb-1">Kode opprettet</div>
              <div className="text-2xl font-black tracking-[0.3em] text-emerald-800 select-all">{created.code}</div>
              <div className="text-[11px] text-emerald-700 mt-1">{created.project_name}{created.label ? ` · ${created.label}` : ""}</div>
            </div>
          ) : null}

          {codes.length > 0 ? (
            <div className="mt-3 pt-3 border-t border-slate-100" data-testid="ts-bclist">
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2">Aktive koder</div>
              <div className="space-y-2">
                {codes.map((c) => (
                  <BreakCodeRow key={c.id} code={c} authHeader={authHeader} onChanged={() => setTick((x) => x + 1)} />
                ))}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

// BreakCodeRow — one active paid-break code with inline edit of its validity
// window / label (so an admin can shorten/extend it when conditions change)
// plus a revoke action.
function BreakCodeRow({ code, authHeader, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [labelTxt, setLabelTxt] = useState(code.label || "");
  const [vf, setVf] = useState(code.valid_from || "");
  const [vt, setVt] = useState(code.valid_to || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const save = async () => {
    setErr("");
    if (vf && vt && vf > vt) { setErr("Gyldig fra må være før gyldig til."); return; }
    setBusy(true);
    try {
      await axios.put(`${TS}/admin/break-codes/${code.id}`, { label: labelTxt.trim(), valid_from: vf, valid_to: vt }, authHeader());
      setEditing(false);
      onChanged();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Kunne ikke lagre.");
    }
    setBusy(false);
  };

  const revoke = async () => {
    if (!window.confirm("Trekke tilbake denne koden?")) return;
    setBusy(true);
    try {
      await axios.delete(`${TS}/admin/break-codes/${code.id}`, authHeader());
      onChanged();
    } catch { /* silent */ }
    setBusy(false);
  };

  const range = `${code.valid_from || "…"} – ${code.valid_to || "…"}`;

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-2.5" data-testid={`ts-bcrow-${code.code}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="font-black tracking-[0.2em] text-slate-800 text-sm">{code.code}</div>
          <div className="text-[11px] text-slate-500 truncate">{code.project_name}{code.label ? ` · ${code.label}` : ""}</div>
          <div className="text-[11px] text-slate-400">{range}</div>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <button onClick={() => setEditing((e) => !e)} disabled={busy} className="text-[11px] font-bold px-2 py-1 rounded-lg bg-white border border-slate-200 text-slate-600 hover:bg-slate-100" data-testid={`ts-bcrow-edit-${code.code}`}>Rediger</button>
          <button onClick={revoke} disabled={busy} className="text-[11px] font-bold px-2 py-1 rounded-lg bg-red-50 border border-red-200 text-red-600 hover:bg-red-100" data-testid={`ts-bcrow-revoke-${code.code}`}>Trekk tilbake</button>
        </div>
      </div>
      {editing ? (
        <div className="mt-2.5 space-y-2 border-t border-slate-200 pt-2.5">
          <input value={labelTxt} onChange={(e) => setLabelTxt(e.target.value)} placeholder="Merkelapp" className="w-full text-sm border border-slate-200 rounded-lg px-2 py-1.5" data-testid={`ts-bcrow-label-${code.code}`} />
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1">Gyldig fra</label>
              <input type="date" value={vf} onChange={(e) => setVf(e.target.value)} className="w-full text-sm border border-slate-200 rounded-lg px-2 py-1.5" data-testid={`ts-bcrow-from-${code.code}`} />
            </div>
            <div>
              <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1">Gyldig til</label>
              <input type="date" value={vt} onChange={(e) => setVt(e.target.value)} className="w-full text-sm border border-slate-200 rounded-lg px-2 py-1.5" data-testid={`ts-bcrow-to-${code.code}`} />
            </div>
          </div>
          {err ? <div className="text-xs font-bold text-red-600">{err}</div> : null}
          <button onClick={save} disabled={busy} className="w-full inline-flex items-center justify-center gap-1.5 text-sm font-black text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 rounded-lg py-2" data-testid={`ts-bcrow-save-${code.code}`}>
            {busy ? "Lagrer…" : "Lagre"}
          </button>
        </div>
      ) : null}
    </div>
  );
}

// BreakCodeLog — per-code usage report (for invoicing). Groups paid-break code
// applications by code for the selected month: label, project, valid range,
// how many workers, total uses, and who/when. CSV export. GET break-code-summary.
// -----------------------------------------------------------------------------
function BreakCodeLog({ authHeader, t, month }) {
  const [codes, setCodes] = useState(null);
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [projFilter, setProjFilter] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/doserne/timesheet/admin/break-code-summary`, { params: { month }, ...authHeader() });
        if (alive) setCodes(data.codes || []);
      } catch { if (alive) setCodes([]); }
    })();
    return () => { alive = false; };
  }, [authHeader, month]);

  if (codes === null) return null;

  const projects = [...new Set(codes.map((c) => c.project_name).filter(Boolean))].sort();
  const shown = projFilter ? codes.filter((c) => c.project_name === projFilter) : codes;

  const exportCsv = () => {
    const rows = [["Code", "Label", "Project", "Valid from", "Valid to", "Employee", "Date"]];
    shown.forEach((c) => (c.applications || []).forEach((a) => rows.push([c.code, c.label || "", c.project_name || "", c.valid_from || "", c.valid_to || "", a.user_name || a.user_email || "", a.date || ""])));
    const csv = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
    const a = document.createElement("a");
    a.href = url; a.download = `break-codes-${month}${projFilter ? "-" + projFilter : ""}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="mb-4 rounded-xl border border-slate-200 bg-white p-3 shadow-sm" data-testid="ts-bclog">
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between gap-2" data-testid="ts-bclog-toggle">
        <div className="inline-flex items-center gap-1.5 text-[11px] font-black uppercase tracking-wider text-slate-500">
          <Coins className="w-3.5 h-3.5 text-amber-500" /> {t("ts.bclog_title")}
          <span className="ml-1 px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px]">{shown.length}</span>
        </div>
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open ? (
        codes.length === 0 ? (
          <div className="mt-2 text-sm text-slate-400 font-semibold" data-testid="ts-bclog-empty">{t("ts.bclog_none")}</div>
        ) : (
          <div className="mt-3 space-y-2">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <select
                value={projFilter}
                onChange={(e) => setProjFilter(e.target.value)}
                className="text-xs font-bold border border-slate-200 rounded-lg px-2 py-1.5 bg-white max-w-[200px]"
                data-testid="ts-bcsum-projfilter"
              >
                <option value="">{t("ts.bcsum_all_projects")}</option>
                {projects.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
              <button onClick={exportCsv} className="inline-flex items-center gap-1 text-[11px] font-bold text-indigo-600 hover:text-indigo-700 border border-indigo-200 rounded-lg px-2 py-1" data-testid="ts-bcsum-export">
                <Printer className="w-3.5 h-3.5" /> {t("ts.bcsum_export")}
              </button>
            </div>
            {shown.map((c) => (
              <div key={c.code} className="rounded-xl border border-slate-100 bg-slate-50/60 p-2.5" data-testid={`ts-bcsum-${c.code}`}>
                <button onClick={() => setExpanded((e) => ({ ...e, [c.code]: !e[c.code] }))} className="w-full flex items-center justify-between gap-2 text-left">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-black text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded text-sm tracking-wider">{c.code}</span>
                    <span className="text-xs font-bold text-slate-700">{c.project_name}</span>
                    <span className="text-[11px] font-semibold text-indigo-600">🏷️ {c.label || t("ts.bcsum_nolabel")}</span>
                  </div>
                  <div className="flex items-center gap-2 text-[11px] font-bold text-slate-500 shrink-0">
                    <span className="text-emerald-700">{c.worker_count} {t("ts.bcsum_workers")}</span>
                    <span className="text-cyan-700">{c.application_count} {t("ts.bcsum_uses")}</span>
                    <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${expanded[c.code] ? "rotate-180" : ""}`} />
                  </div>
                </button>
                <div className="text-[11px] text-slate-400 font-semibold mt-1">{t("ts.bcsum_valid")}: {c.valid_from || "…"} → {c.valid_to || "…"}</div>
                {expanded[c.code] ? (
                  <div className="mt-2 overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-left text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-100">
                          <th className="py-1 pr-3 font-black">{t("ts.bclog_who")}</th>
                          <th className="py-1 font-black">{t("ts.bclog_day")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(c.applications || []).map((a, i) => (
                          <tr key={i} className="border-b border-slate-50">
                            <td className="py-1 pr-3 font-bold text-slate-700">{a.user_name || a.user_email}</td>
                            <td className="py-1 text-slate-600 font-semibold">{a.date}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        )
      ) : null}
    </div>
  );
}



// SalaryExportPanel — generates the Tripletex "Lønnsimport" CSV for the month
// (approved/locked hours only). Lets the admin set the lønnsart numbers and fix
// missing ANSATTNR inline, then downloads the ready-to-import file.
const LONNSART_FIELDS = [
  ["lonnsart_normal", "Ore normale"],
  ["lonnsart_ot50", "Overtime 50%"],
  ["lonnsart_ot100", "Overtime 100%"],
  ["lonnsart_sick", "Sykepenger"],
  ["lonnsart_leader", "Tillegg bas"],
];

function SalaryExportPanel({ authHeader, month }) {
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [savingArt, setSavingArt] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [tick, setTick] = useState(0);
  // Tripletex-sourced workers: `sources` lists each member with a Tripletex
  // ratio; `excluded` is the admin's current confirmed set (pre-ticked from
  // `suggest_exclude`). The CSV download sends this set as `exclude_emails`.
  const [sources, setSources] = useState([]);
  const [excluded, setExcluded] = useState(new Set());

  useEffect(() => {
    if (!open) return;
    let alive = true;
    axios.get(`${TS}/admin/settings`, authHeader())
      .then((r) => { if (alive) setSettings(r.data); })
      .catch(() => { if (alive) setSettings({}); });
    axios.get(`${TS}/admin/salary-export/tripletex-sources`, { params: { month }, ...authHeader() })
      .then((r) => {
        if (!alive) return;
        setSources(r.data.members || []);
        setExcluded(new Set((r.data.members || []).filter((m) => m.suggest_exclude).map((m) => m.email)));
      })
      .catch(() => { if (alive) { setSources([]); setExcluded(new Set()); } });
    return () => { alive = false; };
  }, [open, authHeader, month, tick]);

  const toggleExcluded = (email) => {
    setExcluded((prev) => {
      const next = new Set(prev);
      if (next.has(email)) next.delete(email); else next.add(email);
      return next;
    });
  };

  const togglePersistent = async (email, currentlyTripletex) => {
    const nextSrc = currentlyTripletex ? "doserne" : "tripletex";
    try {
      await axios.put(`${TS}/admin/user/${encodeURIComponent(email)}/payroll-source`, null, {
        params: { source: nextSrc }, ...authHeader(),
      });
      toast.success(nextSrc === "tripletex" ? "Marcat ca Tripletex" : "Readus în Doserne");
      setTick((x) => x + 1);
    } catch { toast.error("Nu am putut salva"); }
  };

  useEffect(() => {
    if (!open) return;
    let alive = true;
    setBusy(true);
    const ex = Array.from(excluded).join(",");
    axios.get(`${TS}/admin/salary-export/preview`, { params: { month, exclude_emails: ex }, ...authHeader() })
      .then((r) => { if (alive) setPreview(r.data); })
      .catch(() => { if (alive) setPreview(null); })
      .finally(() => { if (alive) setBusy(false); });
    return () => { alive = false; };
  }, [open, month, authHeader, tick, excluded]);

  const saveArt = async () => {
    setSavingArt(true);
    try {
      const payload = {};
      LONNSART_FIELDS.forEach(([k]) => { payload[k] = settings?.[k] || ""; });
      await axios.put(`${TS}/admin/settings`, payload, authHeader());
      toast.success("Lønnsarter lagret");
      setTick((x) => x + 1);
    } catch { toast.error("Kunne ikke lagre"); }
    setSavingArt(false);
  };

  const saveAnsattnr = async (email, value) => {
    try {
      await axios.put(`${TS}/admin/member-ansattnr`, { email, ansattnr: value }, authHeader());
      setTick((x) => x + 1);
    } catch { toast.error("Kunne ikke lagre ansattnr"); }
  };

  const download = async () => {
    setDownloading(true);
    try {
      const ex = Array.from(excluded).join(",");
      const r = await axios.get(`${TS}/admin/salary-export/csv`, { params: { month, exclude_emails: ex }, responseType: "blob", ...authHeader() });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = `tripletex_lonn_${month}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Kunne ikke laste ned"); }
    setDownloading(false);
  };

  return (
    <div className="mb-4 rounded-xl border border-slate-200 bg-white p-3 shadow-sm" data-testid="ts-salary-export">
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between gap-2" data-testid="ts-salary-export-toggle">
        <div className="inline-flex items-center gap-1.5 text-[11px] font-black uppercase tracking-wider text-slate-500">
          <Download className="w-3.5 h-3.5 text-indigo-500" /> Export Tripletex lønn
        </div>
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open ? (
        <div className="mt-3 space-y-3">
          <div className="rounded-lg bg-slate-50 border border-slate-200 p-2.5">
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2">Lønnsartnummer</div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
              {LONNSART_FIELDS.map(([k, lbl]) => (
                <div key={k}>
                  <label className="block text-[10px] font-bold text-slate-500 mb-1">{lbl}</label>
                  <input
                    value={settings?.[k] ?? ""}
                    onChange={(e) => setSettings((s) => ({ ...s, [k]: e.target.value }))}
                    className="w-full text-sm border border-slate-200 rounded-lg px-2 py-1.5"
                    data-testid={`ts-salary-art-${k}`}
                  />
                </div>
              ))}
            </div>
            <button onClick={saveArt} disabled={savingArt} className="mt-2 inline-flex items-center gap-1.5 text-xs font-black text-white bg-slate-700 hover:bg-slate-800 disabled:opacity-60 rounded-lg px-3 py-1.5" data-testid="ts-salary-art-save">
              {savingArt ? "Lagrer…" : "Lagre lønnsarter"}
            </button>
          </div>

          {sources.length > 0 && (
            <div className="rounded-lg bg-indigo-50/40 border border-indigo-200 p-2.5" data-testid="ts-salary-tripletex-list">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] font-black uppercase tracking-widest text-indigo-700">Angajați plătiți prin Tripletex (excluși din CSV)</div>
                <span className="text-[10px] font-black text-indigo-600">{excluded.size} / {sources.filter((m) => m.total_entries > 0).length} bifați</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                {sources.filter((m) => m.total_entries > 0 || m.payroll_source === "tripletex").map((m) => {
                  const checked = excluded.has(m.email);
                  const persistent = m.payroll_source === "tripletex";
                  return (
                    <label key={m.email} className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg border ${checked ? "bg-white border-indigo-300" : "bg-white/50 border-slate-200"} cursor-pointer`}>
                      <input type="checkbox" checked={checked} onChange={() => toggleExcluded(m.email)} data-testid={`ts-salary-exclude-${m.email}`} />
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-black text-slate-800 truncate">{m.name}</div>
                        <div className="text-[10px] text-slate-500">
                          {m.tripletex_entries}/{m.total_entries} intrări din Tripletex · {Math.round(m.ratio * 100)}%
                        </div>
                      </div>
                      <button type="button" onClick={(e) => { e.preventDefault(); togglePersistent(m.email, persistent); }}
                        className={`text-[10px] font-black px-1.5 py-0.5 rounded ${persistent ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
                        title={persistent ? "Click: readu-l în Doserne" : "Click: marchează permanent ca Tripletex"}>
                        {persistent ? "📌 Tripletex" : "📌"}
                      </button>
                    </label>
                  );
                })}
              </div>
              <div className="mt-2 text-[10px] text-indigo-700/70">
                💡 Angajații cu <b>≥95% ore din Tripletex</b> au bifa pusă automat. Click pe 📌 ca să-l marchezi permanent (va fi exclus din toate exporturile viitoare).
              </div>
            </div>
          )}

          {preview?.missing_count > 0 ? (
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-2.5 text-xs font-bold text-amber-700" data-testid="ts-salary-missing">
              ⚠ {preview.missing_count} ansatt(e) mangler ANSATTNR — fyll inn under for å ta dem med i filen.
            </div>
          ) : null}

          {busy ? (
            <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
          ) : preview && preview.rows?.length ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] font-black uppercase tracking-widest text-slate-400 text-left border-b border-slate-200">
                    <th className="py-1.5 pr-2">Ansatt</th>
                    <th className="py-1.5 pr-2">Ansattnr</th>
                    <th className="py-1.5 pr-2 text-right">Normal</th>
                    <th className="py-1.5 pr-2 text-right">OT50</th>
                    <th className="py-1.5 pr-2 text-right">OT100</th>
                    <th className="py-1.5 pr-2 text-right">Syk</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((r) => {
                    const h = (kind) => { const ln = r.lines.find((x) => x.kind === kind); return ln ? ln.hours : 0; };
                    return (
                      <tr key={r.email} className={`border-b border-slate-100 ${r.missing_ansattnr ? "bg-amber-50/50" : ""}`} data-testid={`ts-salary-row-${r.email}`}>
                        <td className="py-1.5 pr-2 font-semibold text-slate-700">{r.name}</td>
                        <td className="py-1.5 pr-2">
                          <input
                            defaultValue={r.ansattnr}
                            onBlur={(e) => { const v = e.target.value.trim(); if (v !== (r.ansattnr || "")) saveAnsattnr(r.email, v); }}
                            placeholder="—"
                            className={`w-20 text-sm border rounded-lg px-2 py-1 ${r.missing_ansattnr ? "border-amber-400" : "border-slate-200"}`}
                            data-testid={`ts-salary-ansattnr-${r.email}`}
                          />
                        </td>
                        <td className="py-1.5 pr-2 text-right tabular-nums">{h("normal") || "–"}</td>
                        <td className="py-1.5 pr-2 text-right tabular-nums">{h("ot50") || "–"}</td>
                        <td className="py-1.5 pr-2 text-right tabular-nums">{h("ot100") || "–"}</td>
                        <td className="py-1.5 pr-2 text-right tabular-nums">{h("sick") || "–"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-xs text-slate-400 py-3 text-center">Ingen godkjente (låste) timer denne måneden. Lås ukene først.</div>
          )}

          <button onClick={download} disabled={downloading || !preview?.rows?.length} className="w-full inline-flex items-center justify-center gap-1.5 text-sm font-black text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 rounded-lg py-2.5" data-testid="ts-salary-download">
            {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Last ned CSV ({monthLabel(month)})
          </button>
        </div>
      ) : null}
    </div>
  );
}
